/**
 * Webinars.tsx — Comprehensive 11-section webinar performance dashboard.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, ReferenceLine,
} from "recharts";
import { branding } from "../config/branding";

// ─── Types ───────────────────────────────────────────────────────────────────

interface WebinarKPIs {
  totalOptins: number; totalAttended: number; showRate: number; avgDuration: number;
  totalReachedPitch: number; reachedPitchRate: number;
  totalReachedEndOfPitch: number; reachedEndOfPitchRate: number;
  totalStayed5: number; stayed5Rate: number;
  totalApplications: number; optinToAppRate: number; attendeeToAppRate: number;
  totalCallsHad: number;
  closedDeals: number; revenue: number;
}

interface ComparisonRow {
  webinarId: string; webinarName: string; webinarDate: string | null;
  audience: string; offerAngle: string;
  optins: number; attended: number; showRate: number; avgDuration: number;
  reachedPitch: number; reachedPitchRate: number;
  reachedEndOfPitch: number; reachedEndOfPitchRate: number;
  stayed5: number; stayed5Rate: number;
  applications: number; optinToAppRate: number; attendeeToAppRate: number;
  callsHad: number; callToCloseRate: number;
  closedDeals: number; revenue: number;
}

interface FilterOptions {
  webinars: Array<{ id: string; name: string; date: string | null }>;
  trafficTypes: string[]; audienceTemps: string[];
  offerAngles: string[]; utmCampaigns: string[];
}

interface CohortRow {
  optinDate: string; daysBeforeWebinar: number | null; bucket: string;
  optins: number; attended: number; showRate: number; avgDuration: number;
  reachedPitchRate: number; applications: number; optinToAppRate: number; attendeeToAppRate: number;
}

interface TimingBucketRow {
  bucket: string; optins: number; attended: number; showRate: number; avgDuration: number;
  reachedPitch: number; reachedPitchRate: number;
  reachedEndOfPitch: number; reachedEndOfPitchRate: number;
  stayed5: number; stayed5Rate: number;
  applications: number; optinToAppRate: number; attendeeToAppRate: number;
  closedDeals: number; revenue: number;
}

interface HeatmapCell {
  showRate: number; optinToAppRate: number; attendeeToAppRate: number;
  reachedPitchRate: number; optins: number;
  attended: number; applications: number; closedDeals: number; revenue: number;
}

interface HeatmapData {
  webinars: Array<{ id: string; name: string }>;
  buckets: string[];
  cells: Record<string, Record<string, HeatmapCell>>;
}

interface BreakdownRow {
  key: string; optins: number; attended: number; showRate: number;
  reachedPitchRate: number; applications: number; optinToAppRate: number; attendeeToAppRate: number;
  closedDeals: number;
}

interface AppTimingRow {
  optinDate: string; optins: number; applications: number; optinToAppRate: number;
  avgDaysToApplication: number; appliedWithin24h: number; appliedWithin7d: number;
}

interface AppCompareRow {
  bucket: string; optins: number; applications: number; optinToAppRate: number;
  avgDaysToApp: number; within24h: number; within7d: number;
}

interface RepeatData {
  repeatAttendees: number;
  avgWebinarsBeforeApp: number;
  avgWebinarsBeforeClose: number;
  byWebinarsAttended: Array<{
    webinarsAttended: string;
    people: number;
    applied: number;
    appRate: number;
    closed: number;
    closeRate: number;
    avgDaysToApp: number;
    avgDaysToClose: number;
  }>;
}

interface HealthCheck { label: string; count: number; severity: "info" | "warn" | "error" }

interface PipelineDay { date: string; optins: number; applications: number; }
interface PipelineSource { source: string; optins: number; applications: number; calls: number; closed: number; revenue: number; }
interface PipelineData {
  totalOptins: number; totalApplications: number; totalCalls: number;
  totalClosed: number; totalRevenue: number;
  showRate: number; appRate: number; closeRate: number;
  daily: PipelineDay[];
  bySource: PipelineSource[];
  timeline: Array<{ week: string; optins: number }>;
}

interface ActiveFilters {
  webinarId: string; startDate: string; endDate: string;
  trafficType: string; audienceTemp: string; utmCampaign: string; offerAngle: string;
}

type MinutePoint = { minute: number; total: number; [source: string]: number };

interface AttendanceKPIs {
  totalAttendees: number; peakAttendees: number; peakMinute: number;
  at50: number; pctAt50: number; biggestDropWindow: string;
}

interface AttendanceData {
  minuteData: MinutePoint[]; sources: string[];
  kpis: AttendanceKPIs; insights: string[];
  retentionData: MinutePoint[];
  isMock?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const pct = (v: number) => `${v.toFixed(1)}%`;
const num = (v: number) => v.toLocaleString();
const mins = (v: number) => `${v.toFixed(1)}m`;
const dollar = (v: number) =>
  `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const CHART = branding.colors.chart;

// Attendance chart: assign colors to dynamic sources
const ATTENDANCE_COLORS: Record<string, string> = {};
function getSourceColor(src: string, index: number): string {
  if (src === "Unknown") return "rgba(140,140,160,0.85)";
  if (!ATTENDANCE_COLORS[src]) ATTENDANCE_COLORS[src] = CHART[index % CHART.length] ?? CHART[0];
  return ATTENDANCE_COLORS[src];
}
// ─── Mock attendance data (proof of concept — replace with real Zoom data) ──

const MOCK_ATTENDANCE_SOURCES = ["Instagram", "Facebook", "Email", "YouTube"];

function buildMockAttendanceData() {
  // Simulate 4 sources with natural drop-off curves over 90 minutes
  type Raw = { instagram: number; facebook: number; email: number; youtube: number };
  const starts: Raw = { instagram: 112, facebook: 74, email: 51, youtube: 33 };

  // Retention multiplier by minute: fast ramp, slow decay, pitch cliff at ~50, tail off
  function retain(min: number): number {
    if (min <= 5)  return 0.70 + (min / 5) * 0.30;           // ramp 70→100%
    if (min <= 15) return 1.00 - ((min - 5) / 10) * 0.12;    // slow -12%
    if (min <= 30) return 0.88 - ((min - 15) / 15) * 0.10;   // -10%
    if (min <= 45) return 0.78 - ((min - 30) / 15) * 0.08;   // -8%
    if (min <= 55) return 0.70 - ((min - 45) / 10) * 0.22;   // pitch cliff -22%
    if (min <= 70) return 0.48 - ((min - 55) / 15) * 0.14;   // post-pitch -14%
    if (min <= 85) return 0.34 - ((min - 70) / 15) * 0.16;   // final -16%
    return Math.max(0, 0.18 - ((min - 85) / 10) * 0.18);
  }

  // Slight source-specific variance
  const variance: Record<string, (m: number) => number> = {
    Instagram: (m) => m > 40 && m < 60 ? 0.95 : 1.00,
    Facebook:  (m) => m > 50 ? 0.92 : 1.00,
    Email:     (m) => m > 30 ? 1.06 : 1.00,   // email retains better
    YouTube:   (m) => m < 20 ? 0.88 : 1.02,
  };

  const minuteData: MinutePoint[] = [];
  const retentionData: MinutePoint[] = [];

  for (let min = 0; min <= 90; min += 1) {
    const base = retain(min);
    const row: MinutePoint = { minute: min };
    const retRow: MinutePoint = { minute: min };
    for (const src of MOCK_ATTENDANCE_SOURCES) {
      const key = src.toLowerCase() as keyof Raw;
      const count = Math.max(0, Math.round(starts[key] * base * variance[src](min)));
      row[src] = count;
      retRow[src] = Math.round(base * variance[src](min) * 100);
    }
    minuteData.push(row);
    retentionData.push(retRow);
  }

  const totalAttendees = Object.values(starts).reduce((s, v) => s + v, 0);
  const peakMinute = 5;
  const peakAttendees = totalAttendees;
  const at50 = minuteData[50]
    ? MOCK_ATTENDANCE_SOURCES.reduce((s, src) => s + (Number(minuteData[50][src]) || 0), 0)
    : 0;
  const pctAt50 = Math.round((at50 / peakAttendees) * 100);

  return {
    minuteData,
    retentionData,
    sources: MOCK_ATTENDANCE_SOURCES,
    kpis: {
      totalAttendees,
      peakAttendees,
      peakMinute,
      at50,
      pctAt50,
      biggestDropWindow: "Min 45–55",
    },
    insights: [
      `Email subscribers had the highest retention past minute 30 — staying ~6% above average.`,
      `The biggest audience drop occurred around minute 45–55 (the pitch window): ${100 - pctAt50}% of peak viewers dropped off.`,
      `Instagram drove the most attendees (${starts.instagram}) but had the steepest drop at the pitch.`,
      `YouTube attendees started slightly slower but retained well after the pitch.`,
    ],
    isMock: true,
  };
}

const MOCK_ATTENDANCE = buildMockAttendanceData();



const DEFAULT_FILTERS: ActiveFilters = {
  webinarId: "all", startDate: "", endDate: "",
  trafficType: "", audienceTemp: "", utmCampaign: "", offerAngle: "",
};

function buildUrl(base: string, f: ActiveFilters): string {
  const p = new URLSearchParams();
  if (f.webinarId && f.webinarId !== "all") p.set("webinarId", f.webinarId);
  if (f.startDate) p.set("startDate", f.startDate);
  if (f.endDate) p.set("endDate", f.endDate);
  if (f.trafficType) p.set("trafficType", f.trafficType);
  if (f.audienceTemp) p.set("audienceTemp", f.audienceTemp);
  if (f.utmCampaign) p.set("utmCampaign", f.utmCampaign);
  if (f.offerAngle) p.set("offerAngle", f.offerAngle);
  const qs = p.toString();
  return qs ? `${base}?${qs}` : base;
}

function heatColor(value: number, max: number): string {
  if (max === 0 || value === 0) return "transparent";
  const intensity = Math.min(value / max, 1);
  return `hsla(191, 91%, ${80 - intensity * 50}%, ${0.15 + intensity * 0.7})`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, highlight }: {
  label: string; value: string; sub?: string; highlight?: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border bg-card p-4 flex flex-col gap-1 ${highlight ? "border-[var(--brand-primary)]/40" : ""}`}
    >
      <span className="text-xs text-muted-foreground font-medium">{label}</span>
      <span className={`text-2xl font-bold ${highlight ? "text-[var(--brand-primary)]" : "text-foreground"}`}>
        {value}
      </span>
      {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
    </motion.div>
  );
}

function Section({ title, children, className = "" }: {
  title: string; children: React.ReactNode; className?: string;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border bg-card p-5 ${className}`}
    >
      <h2 className="text-sm font-semibold text-foreground mb-4">{title}</h2>
      {children}
    </motion.section>
  );
}

type SortDir = "asc" | "desc";

function SortTh({
  label, field, sortField, sortDir, onSort,
}: {
  label: string; field: string; sortField: string; sortDir: SortDir;
  onSort: (f: string) => void;
}) {
  const active = field === sortField;
  return (
    <th
      className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground cursor-pointer select-none whitespace-nowrap hover:text-foreground transition-colors"
      onClick={() => onSort(field)}
    >
      {label}{" "}
      {active
        ? sortDir === "asc" ? "↑" : "↓"
        : <span className="opacity-25">↕</span>}
    </th>
  );
}

// ─── Breakdown grouping helpers ──────────────────────────────────────────────

/** Normalize a raw key into a canonical group name */
function normalizeGroupKey(raw: string): string {
  const s = raw.trim().toLowerCase();
  if (!s || s === "unknown" || s === "direct" || s === "(none)" || s === "none") return "Unknown";
  if (s.startsWith("instagram") || s.includes("insta") || s.startsWith("ig ")) return "Instagram";
  if (s.startsWith("youtube") || s.startsWith("yt ")) return "YouTube";
  if (s.startsWith("facebook") || s.startsWith("fb ") || s === "meta") return "Facebook";
  if (s === "email" || s === "emails" || s.startsWith("email ")) return "Email";
  if (s === "paid" || s === "paid ads" || s.includes("paid")) return "Paid Ads";
  if (s === "organic" || s === "seo") return "Organic";
  if (s === "tiktok") return "TikTok";
  if (s === "google" || s.startsWith("google ")) return "Google";
  return raw.trim(); // keep as-is for other sources
}

interface GroupedRow {
  group: string;
  optins: number;
  attended: number;
  applications: number;
  closedDeals: number;
  subRows: BreakdownRow[];
}

function groupBreakdownRows(rows: BreakdownRow[]): GroupedRow[] {
  const map = new Map<string, GroupedRow>();
  for (const r of rows) {
    const g = normalizeGroupKey(r.key);
    if (!map.has(g)) map.set(g, { group: g, optins: 0, attended: 0, applications: 0, closedDeals: 0, subRows: [] });
    const entry = map.get(g)!;
    entry.optins      += r.optins;
    entry.attended    += r.attended;
    entry.applications += r.applications;
    entry.closedDeals += r.closedDeals;
    entry.subRows.push(r);
  }
  return [...map.values()].sort((a, b) => b.optins - a.optins);
}

function BreakdownTable({ rows, keyLabel }: { rows: BreakdownRow[]; keyLabel: string }) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showOther, setShowOther] = useState(false);

  const grouped = groupBreakdownRows(rows);
  const maxOptins = grouped[0]?.optins ?? 1;

  const SMALL_THRESHOLD = 100;
  const mainGroups = grouped.filter(g => g.optins >= SMALL_THRESHOLD);
  const otherGroups = grouped.filter(g => g.optins < SMALL_THRESHOLD);

  // Synthetic "Other" row
  const otherAgg: GroupedRow | null = otherGroups.length > 0 ? {
    group: "Other",
    optins: otherGroups.reduce((s, g) => s + g.optins, 0),
    attended: otherGroups.reduce((s, g) => s + g.attended, 0),
    applications: otherGroups.reduce((s, g) => s + g.applications, 0),
    closedDeals: otherGroups.reduce((s, g) => s + g.closedDeals, 0),
    subRows: otherGroups.flatMap(g => g.subRows),
  } : null;

  const displayGroups = [...mainGroups, ...(otherAgg ? [otherAgg] : [])];

  // Insight cards
  const eligible = mainGroups.filter(g => g.optins >= 100);
  const topVolume = eligible[0] ?? null;
  const bestShow  = eligible.length > 0
    ? eligible.reduce((best, g) => {
        const sr = g.attended > 0 ? g.attended / g.optins : 0;
        const bs = best.attended > 0 ? best.attended / best.optins : 0;
        return sr > bs ? g : best;
      })
    : null;
  const bestApp   = eligible.length > 0
    ? eligible.reduce((best, g) => {
        const ar = g.applications > 0 ? g.applications / g.optins : 0;
        const ba = best.applications > 0 ? best.applications / best.optins : 0;
        return ar > ba ? g : best;
      })
    : null;

  function toggleGroup(g: string) {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g); else next.add(g);
      return next;
    });
  }

  function safeShowRate(g: GroupedRow): number {
    return g.optins > 0 ? (g.attended / g.optins) * 100 : 0;
  }
  function safeAppRate(g: GroupedRow): number {
    return g.optins > 0 ? (g.applications / g.optins) * 100 : 0;
  }

  const insightCards = [
    {
      label: "Top Volume Source",
      value: topVolume ? topVolume.group : "—",
      sub: topVolume ? `${num(topVolume.optins)} opt-ins` : "",
    },
    {
      label: "Best Show Rate",
      value: bestShow ? bestShow.group : "—",
      sub: bestShow ? `${safeShowRate(bestShow).toFixed(1)}% show rate` : "",
    },
    {
      label: "Best App Rate",
      value: bestApp ? bestApp.group : "—",
      sub: bestApp ? `${safeAppRate(bestApp).toFixed(1)}% app rate` : "",
    },
  ];

  if (rows.length === 0) {
    return <p className="text-center text-muted-foreground py-8 text-sm">No data</p>;
  }

  return (
    <div className="space-y-5">
      {/* Insight cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {insightCards.map(c => (
          <div key={c.label} className="rounded-xl border border-border bg-muted/20 px-4 py-3">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">{c.label}</div>
            <div className="text-sm font-bold text-foreground">{c.value}</div>
            {c.sub && <div className="text-[11px] text-muted-foreground mt-0.5">{c.sub}</div>}
          </div>
        ))}
      </div>

    </div>
  );
}

// ─── Executive Funnel v3 ─────────────────────────────────────────────────────

type SrcKey = "paid" | "organic" | "email" | "unknown" | "other";
const SRC_ORDER: SrcKey[] = ["paid", "organic", "email", "unknown", "other"];
const SRC_LABELS: Record<SrcKey, string> = {
  paid: "Paid", organic: "Organic", email: "Email", unknown: "Unknown", other: "Other",
};
const SRC_COLORS: Record<SrcKey, string> = {
  paid:    branding.colors.chart[3],
  organic: branding.colors.chart[1],
  email:   branding.colors.chart[2],
  unknown: "rgba(140,140,160,0.75)",
  other:   branding.colors.chart[0],
};

function classifySrc(src: string): SrcKey {
  const s = src.toLowerCase();
  if (!s || s === "unknown" || s === "direct" || s === "(none)" || s === "none") return "unknown";
  if (s.includes("paid") || s.includes("facebook") || s.includes("meta") || s.includes("google") ||
      s.includes("tiktok") || s.includes("ig") || s.includes("insta") || s.includes("ad")) return "paid";
  if (s.includes("email") || s.includes("newsletter") || s.includes("drip") || s.includes("mail")) return "email";
  if (s.includes("organic") || s.includes("seo") || s.includes("blog") || s.includes("referral")) return "organic";
  return "other";
}

function mkSplit(): Record<SrcKey, number> {
  return { paid: 0, organic: 0, email: 0, unknown: 0, other: 0 };
}

function buildSplit(rows: BreakdownRow[], field: "optins" | "attended" | "applications" | "closedDeals"): Record<SrcKey, number> {
  const s = mkSplit();
  for (const row of rows) {
    const v = row[field];
    if (typeof v === "number") s[classifySrc(row.key)] += v;
  }
  return s;
}

interface FunnelStage {
  key: string; label: string; total: number;
  bySource: Record<SrcKey, number>;
}

interface FunnelConn {
  label: string;
  rateNum: number; rateDen: number;
  from: FunnelStage; to: FunnelStage;
}

type FunnelTip =
  | { kind: "bar"; stage: FunnelStage; cx: number; cy: number }
  | { kind: "conn"; conn: FunnelConn; cx: number; cy: number };

const MAX_BAR_H = 240;
const MIN_BAR_H = 32;
const CONN_W    = 56;
const LABEL_H   = 26;
const COUNT_H   = 28;

function barHeight(count: number, maxCount: number): number {
  if (maxCount === 0 || count === 0) return MIN_BAR_H;
  return Math.max(MIN_BAR_H, Math.round((count / maxCount) * MAX_BAR_H));
}

function ExecFunnelBar({
  stage, barH, hovered,
  onMouseEnter, onMouseMove, onMouseLeave,
}: {
  stage: FunnelStage; barH: number; hovered: boolean;
  onMouseEnter: (e: React.MouseEvent) => void;
  onMouseMove:  (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
}) {
  return (
    <div className="flex-1 min-w-0 flex flex-col items-center">
      {/* Stage label at top */}
      <div style={{ height: LABEL_H }} className="flex items-center justify-center w-full">
        <span className="text-[11px] font-semibold text-muted-foreground/80 text-center leading-tight truncate px-1">
          {stage.label}
        </span>
      </div>

      {/* Fixed-height container, bar sits at bottom (bottom-aligned) */}
      <div className="w-full flex flex-col justify-end" style={{ height: MAX_BAR_H }}>
        <div
          className="w-full rounded-t-md overflow-hidden cursor-pointer transition-all duration-150"
          style={{
            height: barH,
            opacity: hovered ? 1 : 0.86,
            boxShadow: hovered
              ? "0 0 0 2px rgba(255,255,255,0.10), 0 4px 20px rgba(0,0,0,0.28)"
              : "0 2px 8px rgba(0,0,0,0.12)",
          }}
          onMouseEnter={onMouseEnter}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
        >
          {SRC_ORDER.map(src => {
            const count = stage.bySource[src];
            const h = stage.total > 0 ? (count / stage.total) * barH : 0;
            if (h < 0.5) return null;
            return <div key={src} style={{ height: h, background: SRC_COLORS[src] }} />;
          })}
        </div>
      </div>

      {/* Count below bar */}
      <div style={{ height: COUNT_H }} className="flex items-start justify-center pt-1 w-full">
        <span className="text-[14px] font-black text-foreground tabular-nums leading-none">
          {stage.total.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

function ExecFunnelConnector({
  conn, leftBarH, rightBarH, hovered,
  onMouseEnter, onMouseMove, onMouseLeave,
}: {
  conn: FunnelConn; leftBarH: number; rightBarH: number; hovered: boolean;
  onMouseEnter: (e: React.MouseEvent) => void;
  onMouseMove:  (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
}) {
  const rate    = conn.rateDen > 0 ? (conn.rateNum / conn.rateDen) * 100 : null;
  const rateStr = rate !== null ? `${rate.toFixed(1)}%` : "N/A";

  // Bars are bottom-aligned inside MAX_BAR_H space
  const leftOffset  = MAX_BAR_H - leftBarH;
  const rightOffset = MAX_BAR_H - rightBarH;

  const paths: Array<{ src: SrcKey; d: string }> = [];
  let lCum = 0, rCum = 0;
  for (const src of SRC_ORDER) {
    const lCount = conn.from.bySource[src];
    const rCount = conn.to.bySource[src];
    const lH = conn.from.total > 0 ? (lCount / conn.from.total) * leftBarH  : 0;
    const rH = conn.to.total   > 0 ? (rCount / conn.to.total)   * rightBarH : 0;
    if (lH >= 0.5 || rH >= 0.5) {
      const lY1 = leftOffset  + lCum;
      const lY2 = leftOffset  + lCum + lH;
      const rY1 = rightOffset + rCum;
      const rY2 = rightOffset + rCum + rH;
      const mx  = CONN_W / 2;
      paths.push({
        src,
        d: `M 0 ${lY1} C ${mx} ${lY1},${mx} ${rY1},${CONN_W} ${rY1} L ${CONN_W} ${rY2} C ${mx} ${rY2},${mx} ${lY2},0 ${lY2} Z`,
      });
    }
    lCum += lH;
    rCum += rH;
  }

  return (
    <div
      className="flex-shrink-0 flex flex-col items-center cursor-pointer"
      style={{ width: CONN_W }}
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      {/* Rate label at top — aligns with LABEL_H */}
      <div style={{ height: LABEL_H }} className="flex flex-col items-center justify-center">
        <span className="text-[12px] font-black text-[var(--brand-primary)] leading-none tabular-nums">
          {rateStr}
        </span>
        <span className="text-[8px] font-medium text-muted-foreground/60 leading-none mt-0.5 uppercase tracking-wide">
          {conn.label}
        </span>
      </div>

      {/* SVG connector — same height as MAX_BAR_H */}
      <svg width={CONN_W} height={MAX_BAR_H} style={{ display: "block" }}>
        {paths.map(p => (
          <path
            key={p.src}
            d={p.d}
            fill={SRC_COLORS[p.src]}
            opacity={hovered ? 0.60 : 0.30}
          />
        ))}
      </svg>

      {/* Spacer to match COUNT_H */}
      <div style={{ height: COUNT_H }} />
    </div>
  );
}

function BarTooltipContent({ stage }: { stage: FunnelStage }) {
  return (
    <>
      <div className="font-bold text-[13px] text-foreground mb-2">{stage.label}</div>
      <div className="text-xs text-muted-foreground mb-2.5">
        Total: <span className="font-bold text-foreground">{stage.total.toLocaleString()}</span>
      </div>
      <div className="space-y-1.5">
        {SRC_ORDER.filter(s => stage.bySource[s] > 0).map(src => {
          const count = stage.bySource[src];
          const p = stage.total > 0 ? ((count / stage.total) * 100).toFixed(1) : "0.0";
          return (
            <div key={src} className="flex items-center gap-2 text-xs">
              <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: SRC_COLORS[src] }} />
              <span className="flex-1 text-muted-foreground">{SRC_LABELS[src]}</span>
              <span className="font-semibold text-foreground tabular-nums">{count.toLocaleString()}</span>
              <span className="text-muted-foreground/60 tabular-nums w-10 text-right">{p}%</span>
            </div>
          );
        })}
      </div>
    </>
  );
}

function ConnTooltipContent({ conn }: { conn: FunnelConn }) {
  const aggRate = conn.rateDen > 0 ? (conn.rateNum / conn.rateDen) * 100 : null;
  const aggDrop = aggRate !== null ? 100 - aggRate : null;
  return (
    <>
      <div className="font-bold text-[13px] text-foreground mb-1">
        {conn.from.label} → {conn.to.label}
      </div>
      <div className="text-[11px] text-muted-foreground mb-2 tabular-nums">
        {conn.from.total.toLocaleString()} → {conn.to.total.toLocaleString()}
      </div>
      <div className="flex gap-3 mb-3 text-xs">
        {aggRate !== null && (
          <span>
            <span className="text-muted-foreground">{conn.label}: </span>
            <span className="font-bold text-[var(--brand-primary)]">{aggRate.toFixed(1)}%</span>
          </span>
        )}
        {aggDrop !== null && (
          <span>
            <span className="text-muted-foreground">Lost: </span>
            <span className="font-bold" style={{ color: "rgba(239,68,68,0.9)" }}>{aggDrop.toFixed(1)}%</span>
          </span>
        )}
      </div>
      <div className="border-t border-border/40 pt-2 space-y-1.5">
        {SRC_ORDER.filter(s => conn.from.bySource[s] > 0 || conn.to.bySource[s] > 0).map(src => {
          const fCount = conn.from.bySource[src];
          const tCount = conn.to.bySource[src];
          const conv = fCount > 0 ? `${((tCount / fCount) * 100).toFixed(1)}%` : "N/A";
          const lost = fCount > 0 ? `${(((fCount - tCount) / fCount) * 100).toFixed(1)}%` : "N/A";
          return (
            <div key={src} className="flex items-start gap-1.5 text-[11px]">
              <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0 mt-0.5" style={{ background: SRC_COLORS[src] }} />
              <div className="flex-1 min-w-0">
                <span className="font-medium text-foreground">{SRC_LABELS[src]}</span>
                <div className="text-muted-foreground/70 mt-0.5 tabular-nums">
                  {fCount.toLocaleString()} → {tCount.toLocaleString()}
                  <span className="text-[var(--brand-primary)] ml-1.5">{conv} conv</span>
                  <span className="ml-1" style={{ color: "rgba(239,68,68,0.75)" }}>{lost} lost</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function ExecFunnelSection({ kpis, sources }: { kpis: WebinarKPIs; sources: BreakdownRow[] }) {
  const [tip, setTip] = useState<FunnelTip | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const s = (a: number, b: number) => b > 0 ? (a / b) * 100 : 0;

  const stages: FunnelStage[] = [
    { key: "optins",   label: "Opt-ins",  total: kpis.totalOptins,        bySource: buildSplit(sources, "optins") },
    { key: "attended", label: "Attended", total: kpis.totalAttended,      bySource: buildSplit(sources, "attended") },
    { key: "applied",  label: "Applied",  total: kpis.totalApplications,  bySource: buildSplit(sources, "applications") },
    { key: "closed",   label: "Closed",   total: kpis.closedDeals,        bySource: buildSplit(sources, "closedDeals") },
  ];

  const conns: FunnelConn[] = [
    { label: "Show Rate",   rateNum: kpis.totalAttended,      rateDen: kpis.totalOptins,       from: stages[0], to: stages[1] },
    { label: "App Rate",    rateNum: kpis.totalApplications,  rateDen: kpis.totalOptins,       from: stages[1], to: stages[2] },
    { label: "Close Rate",  rateNum: kpis.closedDeals,        rateDen: kpis.totalApplications, from: stages[2], to: stages[3] },
  ];

  const maxCount  = Math.max(1, ...stages.map(st => st.total));
  const barHeights = stages.map(st => barHeight(st.total, maxCount));

  const showRate  = s(kpis.totalAttended,      kpis.totalOptins);
  const appRate   = s(kpis.totalApplications,  kpis.totalOptins);
  const closeRate = s(kpis.closedDeals,         kpis.totalApplications);

  const kpiChips: Array<{ label: string; value: string; highlight?: boolean }> = [
    { label: "Opt-ins",       value: kpis.totalOptins.toLocaleString() },
    { label: "Attended",      value: kpis.totalAttended.toLocaleString() },
    { label: "Show Rate",     value: `${showRate.toFixed(1)}%`,    highlight: true },
    { label: "Applied",       value: kpis.totalApplications.toLocaleString() },
    { label: "App Rate",      value: `${appRate.toFixed(1)}%`,     highlight: true },
    { label: "Calls Had",     value: kpis.totalCallsHad.toLocaleString() },
    { label: "Closed",        value: kpis.closedDeals.toLocaleString() },
    { label: "Close Rate",    value: `${closeRate.toFixed(1)}%`,   highlight: true },
    ...(kpis.revenue > 0 ? [{ label: "Revenue", value: dollar(kpis.revenue) }] : []),
    ...(kpis.totalApplications > 0 && kpis.revenue > 0
      ? [{ label: "Rev / App", value: dollar(kpis.revenue / kpis.totalApplications) }] : []),
  ];

  function updateTip(e: React.MouseEvent, kind: "bar" | "conn", stage?: FunnelStage, conn?: FunnelConn) {
    const rect = sectionRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    if (kind === "bar" && stage) setTip({ kind: "bar", stage, cx, cy });
    if (kind === "conn" && conn)  setTip({ kind: "conn", conn,  cx, cy });
  }

  return (
    <motion.section
      ref={sectionRef}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border bg-card p-5 relative"
    >
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-2 mb-5">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Webinar Executive Funnel Summary</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Full pipeline from opt-in to close — segmented by traffic source.</p>
        </div>
        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3">
          {SRC_ORDER.map(src => (
            <div key={src} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: SRC_COLORS[src] }} />
              <span className="text-[10px] text-muted-foreground font-medium">{SRC_LABELS[src]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* KPI chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        {kpiChips.map(k => (
          <div
            key={k.label}
            className={`px-3 py-1.5 rounded-lg border text-center min-w-[70px] ${k.highlight
              ? "border-[var(--brand-primary)]/40 bg-[var(--brand-primary)]/5"
              : "border-border bg-muted/30"
            }`}
          >
            <div className="text-[10px] text-muted-foreground font-medium leading-none mb-0.5">{k.label}</div>
            <div className={`text-sm font-bold leading-none ${k.highlight ? "text-[var(--brand-primary)]" : "text-foreground"}`}>
              {k.value}
            </div>
          </div>
        ))}
      </div>

      {/* Funnel visual — fills full width */}
      <div className="w-full">
        <div className="flex items-start w-full">
          {stages.map((stage, i) => (
            <div key={stage.key} className="contents">
              <ExecFunnelBar
                stage={stage}
                barH={barHeights[i]}
                hovered={tip?.kind === "bar" && tip.stage.key === stage.key}
                onMouseEnter={e => updateTip(e, "bar", stage)}
                onMouseMove={e  => updateTip(e, "bar", stage)}
                onMouseLeave={() => setTip(null)}
              />
              {i < stages.length - 1 && (
                <ExecFunnelConnector
                  conn={conns[i]}
                  leftBarH={barHeights[i]}
                  rightBarH={barHeights[i + 1]}
                  hovered={tip?.kind === "conn" && tip.conn.label === conns[i].label}
                  onMouseEnter={e => updateTip(e, "conn", undefined, conns[i])}
                  onMouseMove={e  => updateTip(e, "conn", undefined, conns[i])}
                  onMouseLeave={() => setTip(null)}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Floating tooltip */}
      {tip && (
        <div
          className="absolute z-50 pointer-events-none"
          style={{
            left: tip.cx > 400 ? tip.cx - 264 : tip.cx + 14,
            top: Math.max(0, tip.cy - 16),
          }}
        >
          <div className="rounded-xl border border-border/50 bg-card/98 backdrop-blur-sm p-3 shadow-2xl"
            style={{ width: 252 }}>
            {tip.kind === "bar"
              ? <BarTooltipContent stage={tip.stage} />
              : <ConnTooltipContent conn={tip.conn} />
            }
          </div>
        </div>
      )}
    </motion.section>
  );
}


// ─── Webinar Comparison Tab ───────────────────────────────────────────────────

interface CompManualFields {
  promoStartDate: string; promoEndDate: string; promoDays: string;
  adSpend: string; emailSends: string;
  lpVersion: string; vslVersion: string; offerVersion: string; slideVersion: string;
  replayAvailable: string;
  webinarLength: string; pitchTime: string;
}

type CompManualMap = Record<string, CompManualFields>;
type CompNotesMap  = Record<string, string>;

function emptyManual(): CompManualFields {
  return {
    promoStartDate: "", promoEndDate: "", promoDays: "",
    adSpend: "", emailSends: "",
    lpVersion: "", vslVersion: "", offerVersion: "", slideVersion: "",
    replayAvailable: "",
    webinarLength: "", pitchTime: "",
  };
}

function CompCardSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60 border-b border-border/40 pb-1">
        {title}
      </div>
      {children}
    </div>
  );
}

function CompField({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] text-muted-foreground/70 font-medium">{label}</span>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder ?? "—"}
        className="text-xs bg-muted/30 border border-border/40 rounded px-2 py-1 text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-[var(--brand-primary)]/60 w-full"
      />
    </div>
  );
}

function CompStatCell({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex flex-col items-center gap-0.5 min-w-[60px]">
      <span className={`text-sm font-bold tabular-nums ${highlight ? "text-[var(--brand-primary)]" : "text-foreground"}`}>
        {value}
      </span>
      <span className="text-[9px] text-muted-foreground font-medium text-center leading-tight">{label}</span>
    </div>
  );
}

function ComparisonCard({
  row, expanded, onToggle, note, onNoteChange, manualFields, onManualChange,
  selected, onSelectToggle,
}: {
  row: ComparisonRow;
  expanded: boolean;
  onToggle: () => void;
  note: string;
  onNoteChange: (v: string) => void;
  manualFields: CompManualFields;
  onManualChange: (field: keyof CompManualFields, v: string) => void;
  selected: boolean;
  onSelectToggle: () => void;
}) {
  const sp   = (a: number, b: number) => b > 0 ? (a / b) * 100 : 0;
  const fmt  = (v: number) => v > 0 ? v.toLocaleString() : "—";
  const fmtP = (v: number) => v > 0 ? `${v.toFixed(1)}%` : "—";
  const fmtD = (v: number) => v > 0 ? dollar(v) : "—";

  const closeRate = row.callToCloseRate;

  // Funnel
  type FStage = { label: string; count: number };
  const fStages: FStage[] = [
    { label: "Opt-ins",    count: row.optins },
    { label: "Applied",    count: row.applications },
    { label: "Calls Had",  count: row.callsHad },
    { label: "Closed",     count: row.closedDeals },
  ];
  const fRates = [
    { label: "App Rate",   rate: row.optinToAppRate },
    { label: "Call Rate",  rate: sp(row.callsHad, row.applications) },
    { label: "Close Rate", rate: closeRate },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border bg-card shadow-sm overflow-hidden"
    >
      {/* ── HEADER ── */}
      <div className="px-6 pt-5 pb-5">
        <div className="flex items-start gap-3">
          {/* Compare checkbox */}
          <button
            onClick={e => { e.stopPropagation(); onSelectToggle(); }}
            className={`mt-0.5 shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
              selected
                ? "bg-[var(--brand-primary)] border-[var(--brand-primary)]"
                : "border-border hover:border-[var(--brand-primary)]/60"
            }`}
          >
            {selected && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="var(--background)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          </button>

          {/* Title + expand */}
          <div
            className="flex-1 min-w-0 cursor-pointer"
            onClick={onToggle}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="text-base font-semibold text-foreground leading-tight">{row.webinarName}</span>
                  {row.webinarDate && (
                    <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full shrink-0">
                      {row.webinarDate.slice(0, 10)}
                    </span>
                  )}
                </div>
                {(row.audience || row.offerAngle) && (
                  <div className="text-xs text-muted-foreground/70 flex gap-3 flex-wrap">
                    {row.audience && <span>{row.audience}</span>}
                    {row.offerAngle && <span className="italic">{row.offerAngle}</span>}
                  </div>
                )}
              </div>
              <span className="text-muted-foreground/50 text-sm mt-0.5 shrink-0">{expanded ? "▲" : "▼"}</span>
            </div>

            {/* KPI hierarchy */}
            <div className="mt-4 flex items-end gap-6 flex-wrap">
              <div>
                <div className="text-3xl font-bold text-foreground tabular-nums leading-none">
                  {row.revenue > 0 ? dollar(row.revenue) : "—"}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 font-medium">Revenue</div>
              </div>
              <div className="h-8 w-px bg-border/50 self-center hidden sm:block" />
              <div>
                <div className="text-xl font-bold text-foreground tabular-nums leading-none">{num(row.optins)}</div>
                <div className="text-[10px] text-muted-foreground mt-1 font-medium">Opt-ins</div>
              </div>
              <div className="h-8 w-px bg-border/50 self-center hidden sm:block" />
              <div className="flex items-center gap-2 pb-0.5 flex-wrap">
                <span className="text-sm font-semibold text-foreground tabular-nums">{fmtP(row.showRate)}</span>
                <span className="text-xs text-muted-foreground">show</span>
                <span className="text-muted-foreground/30">·</span>
                <span className="text-sm font-semibold text-foreground tabular-nums">{fmtP(row.optinToAppRate)}</span>
                <span className="text-xs text-muted-foreground">apply</span>
                {closeRate > 0 && (
                  <>
                    <span className="text-muted-foreground/30">·</span>
                    <span className="text-sm font-semibold text-foreground tabular-nums">{fmtP(closeRate)}</span>
                    <span className="text-xs text-muted-foreground">close</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── EXPANDED ── */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="border-t border-border/40" />

            {/* ── FUNNEL (full width) ── */}
            <div className="px-6 py-6">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50 mb-4">Funnel</div>
              <div className="flex items-center w-full">
                {fStages.map((stage, i) => (
                  <div key={stage.label} className="flex items-center flex-1 min-w-0">
                    {/* Stage card — flex-1 to fill */}
                    <div className="flex flex-col items-center flex-1 min-w-0">
                      <div className="w-full rounded-xl border border-border bg-background shadow-sm px-3 py-4 text-center">
                        <div className="text-2xl font-bold tabular-nums text-foreground leading-none">
                          {fmt(stage.count)}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1.5 font-medium">{stage.label}</div>
                      </div>
                    </div>
                    {i < fStages.length - 1 && (
                      <div className="flex flex-col items-center w-[64px] shrink-0">
                        <div className="flex items-center w-full mb-2">
                          <div className="h-px flex-1 bg-border/60" />
                          <svg width="6" height="8" viewBox="0 0 6 8" className="text-border/60 shrink-0">
                            <path d="M0 0 L6 4 L0 8 Z" fill="currentColor"/>
                          </svg>
                        </div>
                        <div className="rounded-md px-2 py-1 text-center shadow-sm" style={{ background: "var(--foreground)" }}>
                          <div className="text-[11px] font-bold tabular-nums" style={{ color: "var(--background)" }}>
                            {fRates[i].rate > 0 ? `${fRates[i].rate.toFixed(1)}%` : "—"}
                          </div>
                        </div>
                        <div className="text-[9px] text-muted-foreground mt-1 text-center whitespace-nowrap">{fRates[i].label}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="mx-6 border-t border-border/30" />

            {/* ── SETUP ── */}
            <div className="px-6 py-5">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50 mb-4">Setup</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">

                {/* A) Promotion */}
                <div className="space-y-2.5">
                  <div className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider mb-2">Promotion</div>
                  <CompField label="Promo Start"   value={manualFields.promoStartDate} onChange={v => onManualChange("promoStartDate", v)} />
                  <CompField label="Promo End"     value={manualFields.promoEndDate}   onChange={v => onManualChange("promoEndDate", v)} />
                  <CompField label="Days of Promo" value={manualFields.promoDays}      onChange={v => onManualChange("promoDays", v)} />
                  <CompField label="Ad Spend ($)"  value={manualFields.adSpend}        onChange={v => onManualChange("adSpend", v)} />
                  <CompField label="Email Sends"   value={manualFields.emailSends}     onChange={v => onManualChange("emailSends", v)} />
                </div>

                {/* B) Assets */}
                <div className="space-y-2.5">
                  <div className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider mb-2">Assets</div>
                  <CompField label="Landing Page Version" value={manualFields.lpVersion}      onChange={v => onManualChange("lpVersion", v)} />
                  <CompField label="VSL Version"          value={manualFields.vslVersion}     onChange={v => onManualChange("vslVersion", v)} />
                  <CompField label="Offer Version"        value={manualFields.offerVersion}   onChange={v => onManualChange("offerVersion", v)} />
                  <CompField label="Slides Version"       value={manualFields.slideVersion}   onChange={v => onManualChange("slideVersion", v)} />
                  <CompField label="Replay Available?"    value={manualFields.replayAvailable} onChange={v => onManualChange("replayAvailable", v)} />
                </div>

                {/* C) Webinar */}
                <div className="space-y-2.5">
                  <div className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider mb-2">Webinar</div>
                  <CompField label="Webinar Length"   value={manualFields.webinarLength} onChange={v => onManualChange("webinarLength", v)} />
                  <CompField label="Pitch Time"       value={manualFields.pitchTime}     onChange={v => onManualChange("pitchTime", v)} />
                  {row.avgDuration > 0 && (
                    <div className="flex items-baseline justify-between gap-2 py-0.5">
                      <span className="text-[10px] text-muted-foreground/70 font-medium">Avg Watch</span>
                      <span className="text-xs font-semibold tabular-nums text-foreground">{row.avgDuration.toFixed(1)}m</span>
                    </div>
                  )}
                  {row.attended > 0 && (
                    <div className="flex items-baseline justify-between gap-2 py-0.5">
                      <span className="text-[10px] text-muted-foreground/70 font-medium">Peak Viewers</span>
                      <span className="text-xs font-semibold tabular-nums text-foreground">{row.attended.toLocaleString()}</span>
                    </div>
                  )}
                  {row.reachedPitchRate > 0 && (
                    <div className="flex items-baseline justify-between gap-2 py-0.5">
                      <span className="text-[10px] text-muted-foreground/70 font-medium">Reached Pitch</span>
                      <span className="text-xs font-semibold tabular-nums text-foreground">{row.reachedPitchRate.toFixed(1)}%</span>
                    </div>
                  )}
                  {row.reachedEndOfPitch > 0 && (
                    <div className="flex items-baseline justify-between gap-2 py-0.5">
                      <span className="text-[10px] text-muted-foreground/70 font-medium">Stayed to End</span>
                      <span className="text-xs font-semibold tabular-nums text-foreground">{row.reachedEndOfPitch.toLocaleString()} ({row.reachedEndOfPitchRate.toFixed(1)}%)</span>
                    </div>
                  )}
                  {row.stayed5Rate > 0 && (
                    <div className="flex items-baseline justify-between gap-2 py-0.5">
                      <span className="text-[10px] text-muted-foreground/70 font-medium">Stayed 5m</span>
                      <span className="text-xs font-semibold tabular-nums text-foreground">{row.stayed5Rate.toFixed(1)}%</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mx-6 border-t border-border/30" />

            {/* ── NOTES ── */}
            <div className="px-6 py-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50 mb-2">Notes</div>
              <textarea
                value={note}
                onChange={e => onNoteChange(e.target.value)}
                placeholder="What changed? What worked? What should we test next?"
                rows={2}
                className="w-full text-sm bg-muted/20 border border-border/40 rounded-xl px-4 py-2.5 text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-[var(--brand-primary)]/60 resize-none leading-relaxed"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}


function WebinarComparisonTab({ compTable }: { compTable: ComparisonRow[] }) {
  const [search,      setSearch]      = useState("");
  const [sortKey,     setSortKey]     = useState<"date" | "revenue" | "optins" | "appRate" | "showRate">("date");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [compareOpen, setCompareOpen] = useState(false);
  const [notes,       setNotes]       = useState<CompNotesMap>({});
  const [manual,      setManual]      = useState<CompManualMap>({});

  useEffect(() => {
    try {
      const n = localStorage.getItem("uu_comparison_notes");
      const m = localStorage.getItem("uu_comparison_manual");
      if (n) setNotes(JSON.parse(n) as CompNotesMap);
      if (m) setManual(JSON.parse(m) as CompManualMap);
    } catch { /* ignore */ }
  }, []);

  function saveNote(id: string, v: string) {
    setNotes(prev => {
      const next = { ...prev, [id]: v };
      try { localStorage.setItem("uu_comparison_notes", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }
  function saveManual(id: string, field: keyof CompManualFields, v: string) {
    setManual(prev => {
      const cur  = prev[id] ?? emptyManual();
      const next = { ...prev, [id]: { ...cur, [field]: v } };
      try { localStorage.setItem("uu_comparison_manual", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }
  function toggleExpand(id: string) {
    setExpandedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleSelect(id: string) {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  const filtered = compTable.filter(r =>
    r.webinarName.toLowerCase().includes(search.toLowerCase())
  );
  const sorted = [...filtered].sort((a, b) => {
    switch (sortKey) {
      case "revenue":  return b.revenue       - a.revenue;
      case "optins":   return b.optins         - a.optins;
      case "appRate":  return b.optinToAppRate - a.optinToAppRate;
      case "showRate": return b.showRate       - a.showRate;
      default:         return (b.webinarDate ?? "").localeCompare(a.webinarDate ?? "");
    }
  });
  const selectedRows = compTable.filter(r => selectedIds.has(r.webinarId));
  const nSelected    = selectedIds.size;

  // ─── Comparison table structure ───────────────────────────────────────────
  type SectionRow = { type: "section"; label: string };
  type DataRow    = { type: "data"; label: string; bold?: boolean; fn: (r: ComparisonRow, m: CompManualFields) => string };
  type TableRow   = SectionRow | DataRow;

  const tableRows: TableRow[] = [
    { type: "section", label: "Identity" },
    { type: "data", label: "Date",          fn: r => r.webinarDate?.slice(0, 10) ?? "—" },
    { type: "data", label: "Audience",      fn: r => r.audience     || "—" },
    { type: "data", label: "Offer / Angle", fn: r => r.offerAngle   || "—" },

    { type: "section", label: "Funnel" },
    { type: "data", label: "Opt-ins",      bold: true, fn: r => r.optins.toLocaleString() },
    { type: "data", label: "Attended",     bold: true, fn: r => r.attended.toLocaleString() },
    { type: "data", label: "Show Rate",    bold: true, fn: r => r.showRate > 0 ? `${r.showRate.toFixed(1)}%` : "—" },
    { type: "data", label: "Applications", bold: true, fn: r => r.applications.toLocaleString() },
    { type: "data", label: "App Rate",     bold: true, fn: r => r.optinToAppRate > 0 ? `${r.optinToAppRate.toFixed(1)}%` : "—" },
    { type: "data", label: "Calls Had",    bold: true, fn: r => r.callsHad.toLocaleString() },
    { type: "data", label: "Call Rate",    bold: true, fn: r => r.applications > 0 && r.callsHad > 0 ? `${((r.callsHad / r.applications) * 100).toFixed(1)}%` : "—" },
    { type: "data", label: "Closed",       bold: true, fn: r => r.closedDeals.toLocaleString() },
    { type: "data", label: "Close Rate",   bold: true, fn: r => r.callToCloseRate > 0 ? `${r.callToCloseRate.toFixed(1)}%` : "—" },

    { type: "section", label: "Results" },
    { type: "data", label: "Revenue",      bold: true, fn: r => r.revenue > 0 ? dollar(r.revenue) : "—" },
    { type: "data", label: "Rev / App",    bold: true, fn: r => r.applications > 0 && r.revenue > 0 ? dollar(r.revenue / r.applications) : "—" },
    { type: "data", label: "Rev / Opt-in", bold: true, fn: r => r.optins > 0 && r.revenue > 0 ? dollar(r.revenue / r.optins) : "—" },

    { type: "section", label: "Webinar" },
    { type: "data", label: "Avg Watch",       fn: r => r.avgDuration > 0 ? `${r.avgDuration.toFixed(1)}m` : "—" },
    { type: "data", label: "Peak Viewers",    fn: r => r.attended > 0 ? r.attended.toLocaleString() : "—" },
    { type: "data", label: "Reached Pitch",   fn: r => r.reachedPitchRate > 0 ? `${r.reachedPitchRate.toFixed(1)}%` : "—" },
    { type: "data", label: "Stayed to End",   fn: r => r.reachedEndOfPitchRate > 0 ? `${r.reachedEndOfPitchRate.toFixed(1)}%` : "—" },
    { type: "data", label: "Stayed 5m",       fn: r => r.stayed5Rate > 0 ? `${r.stayed5Rate.toFixed(1)}%` : "—" },
    { type: "data", label: "Webinar Length",  fn: (_, m) => m.webinarLength || "—" },
    { type: "data", label: "Pitch Time",      fn: (_, m) => m.pitchTime || "—" },

    { type: "section", label: "Promotion" },
    { type: "data", label: "Promo Start",    fn: (_, m) => m.promoStartDate || "—" },
    { type: "data", label: "Promo End",      fn: (_, m) => m.promoEndDate || "—" },
    { type: "data", label: "Days of Promo",  fn: (_, m) => m.promoDays || "—" },
    { type: "data", label: "Ad Spend",       fn: (_, m) => m.adSpend ? `$${m.adSpend}` : "—" },
    { type: "data", label: "Email Sends",    fn: (_, m) => m.emailSends || "—" },

    { type: "section", label: "Assets" },
    { type: "data", label: "Landing Page",   fn: (_, m) => m.lpVersion       || "—" },
    { type: "data", label: "VSL Version",    fn: (_, m) => m.vslVersion      || "—" },
    { type: "data", label: "Offer Version",  fn: (_, m) => m.offerVersion    || "—" },
    { type: "data", label: "Slides",         fn: (_, m) => m.slideVersion    || "—" },
    { type: "data", label: "Replay",         fn: (_, m) => m.replayAvailable || "—" },
  ];

  return (
    <div className="space-y-4">

      {/* ── Comparison table ─────────────────────────────────────── */}
      <AnimatePresence>
        {compareOpen && selectedRows.length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="rounded-2xl border bg-card shadow-lg overflow-hidden"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-border/50 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Side-by-Side Comparison</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{selectedRows.length} webinars</p>
              </div>
              <button onClick={() => setCompareOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors w-7 h-7 flex items-center justify-center rounded-lg hover:bg-muted/40 text-base">
                ✕
              </button>
            </div>

            {/* Scrollable table */}
            <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
              <table className="border-collapse" style={{ width: "max-content", minWidth: "100%" }}>
                <thead className="sticky top-0 z-10">
                  <tr className="border-b border-border bg-card">
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-muted-foreground sticky left-0 bg-card border-r border-border/30 min-w-[140px]">
                      Metric
                    </th>
                    {selectedRows.map(r => (
                      <th key={r.webinarId} className="px-5 py-3 text-left min-w-[180px] max-w-[220px]">
                        <div className="text-xs font-semibold text-foreground truncate" title={r.webinarName}>{r.webinarName}</div>
                        {r.webinarDate && (
                          <div className="text-[10px] text-muted-foreground font-normal mt-0.5">{r.webinarDate.slice(0, 10)}</div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, i) => {
                    if (row.type === "section") {
                      return (
                        <tr key={`sec-${i}`} className="bg-muted/40 border-y border-border/40">
                          <td
                            colSpan={selectedRows.length + 1}
                            className="px-5 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70 sticky left-0"
                          >
                            {row.label}
                          </td>
                        </tr>
                      );
                    }
                    const vals = selectedRows.map(r => row.fn(r, manual[r.webinarId] ?? emptyManual()));
                    const allDash = vals.every(v => v === "—");
                    if (allDash) return null;

                    // Highlight the best numeric value in this row
                    const nums = vals.map(v => {
                      const n = parseFloat(v.replace(/[$,%]/g, "").replace(/,/g, ""));
                      return isNaN(n) ? null : n;
                    });
                    const validNums = nums.filter(n => n !== null) as number[];
                    const maxNum = validNums.length > 1 ? Math.max(...validNums) : null;

                    return (
                      <tr key={row.label} className="border-b border-border/20 hover:bg-muted/10 transition-colors group">
                        <td className="px-5 py-2.5 text-xs text-muted-foreground sticky left-0 bg-card border-r border-border/20 group-hover:bg-muted/10 whitespace-nowrap">
                          {row.label}
                        </td>
                        {selectedRows.map((r, ci) => {
                          const val   = vals[ci];
                          const num   = nums[ci];
                          const isBest = maxNum !== null && num !== null && num === maxNum && validNums.length > 1;
                          return (
                            <td key={r.webinarId} className="px-5 py-2.5 whitespace-nowrap">
                              <span className={`text-sm tabular-nums ${
                                val === "—"
                                  ? "text-muted-foreground/30"
                                  : isBest && row.bold
                                  ? "font-bold text-[var(--brand-primary)]"
                                  : row.bold
                                  ? "font-semibold text-foreground"
                                  : "text-foreground"
                              }`}>
                                {val}
                                {isBest && row.bold && val !== "—" && (
                                  <span className="ml-1.5 text-[9px] font-bold text-[var(--brand-primary)] bg-[var(--brand-primary)]/10 px-1 py-0.5 rounded">
                                    best
                                  </span>
                                )}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Controls ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search webinars…"
          className="text-sm bg-card border rounded-lg px-3 py-1.5 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-[var(--brand-primary)]/60 min-w-[200px]"
        />
        <div className="flex items-center gap-1.5 ml-auto flex-wrap">
          <span className="text-xs text-muted-foreground">Sort:</span>
          {([
            { key: "date",     label: "Newest" },
            { key: "revenue",  label: "Revenue" },
            { key: "optins",   label: "Opt-ins" },
            { key: "appRate",  label: "App Rate" },
            { key: "showRate", label: "Show Rate" },
          ] as const).map(s => (
            <button key={s.key} onClick={() => setSortKey(s.key)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                sortKey === s.key
                  ? "bg-[var(--brand-primary)] text-white"
                  : "border text-muted-foreground hover:text-foreground"
              }`}>
              {s.label}
            </button>
          ))}

          {/* Expand All / Collapse All */}
          <button
            onClick={() => expandedIds.size === 0
              ? setExpandedIds(new Set(sorted.map(r => r.webinarId)))
              : setExpandedIds(new Set())}
            className="ml-2 px-2.5 py-1 rounded text-xs font-medium border text-muted-foreground hover:text-foreground transition-colors"
          >
            {expandedIds.size === 0 ? "Expand All" : "Collapse All"}
          </button>

          {/* Compare button — always visible, activates when ≥1 selected */}
          <button
            onClick={() => nSelected >= 2 && setCompareOpen(o => !o)}
            className={`ml-1 px-2.5 py-1 rounded text-xs font-semibold transition-all ${
              nSelected >= 2
                ? compareOpen
                  ? "bg-[var(--brand-primary)] text-white"
                  : "bg-[var(--brand-primary)]/15 text-[var(--brand-primary)] border border-[var(--brand-primary)]/40 hover:bg-[var(--brand-primary)] hover:text-white"
                : nSelected === 1
                  ? "border border-border text-muted-foreground/60 cursor-default"
                  : "border border-border text-muted-foreground/40 cursor-default"
            }`}
          >
            {nSelected >= 2
              ? `Compare (${nSelected})`
              : nSelected === 1
              ? "Select 1 more"
              : "Compare"}
          </button>

          {/* Clear selection */}
          {nSelected > 0 && (
            <button
              onClick={() => { setSelectedIds(new Set()); setCompareOpen(false); }}
              className="px-2 py-1 rounded text-xs text-muted-foreground hover:text-foreground border border-border transition-colors"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* ── Cards ────────────────────────────────────────────────── */}
      <div className="space-y-3">
        {sorted.map(row => (
          <ComparisonCard
            key={row.webinarId}
            row={row}
            expanded={expandedIds.has(row.webinarId)}
            onToggle={() => toggleExpand(row.webinarId)}
            note={notes[row.webinarId] ?? ""}
            onNoteChange={v => saveNote(row.webinarId, v)}
            manualFields={manual[row.webinarId] ?? emptyManual()}
            onManualChange={(field, v) => saveManual(row.webinarId, field, v)}
            selected={selectedIds.has(row.webinarId)}
            onSelectToggle={() => toggleSelect(row.webinarId)}
          />
        ))}
        {sorted.length === 0 && (
          <div className="py-12 text-center text-muted-foreground text-sm">
            {compTable.length === 0 ? "No webinar data available. Sync your sheets." : "No webinars match your search."}
          </div>
        )}
      </div>
    </div>
  );
}


export function Webinars({ initialTab = "performance" }: { initialTab?: "performance" | "comparison" }) {
  const [webinarMainTab, setWebinarMainTab] = useState<"performance" | "comparison">(initialTab);

  useEffect(() => { setWebinarMainTab(initialTab); }, [initialTab]);
  const [filters, setFilters] = useState<ActiveFilters>(DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    webinars: [], trafficTypes: [], audienceTemps: [], offerAngles: [], utmCampaigns: [],
  });

  const [kpis, setKpis] = useState<WebinarKPIs | null>(null);
  const [compTable, setCompTable] = useState<ComparisonRow[]>([]);
  const [compSortField, setCompSortField] = useState("optins");
  const [compSortDir, setCompSortDir] = useState<SortDir>("desc");
  const [cardSort, setCardSort] = useState<"date" | "showRate" | "appRate" | "revenue" | "optins">("date");
  const [selectedCards, setSelectedCards] = useState<Set<string>>(new Set());
  const [compareMode, setCompareMode] = useState(false);

  const [cohortByDate, setCohortByDate] = useState<CohortRow[]>([]);
  const [daysBeforeBuckets, setDaysBeforeBuckets] = useState<TimingBucketRow[]>([]);
  const [noAppCount, setNoAppCount] = useState(0);
  const [pitchEngagement, setPitchEngagement] = useState<TimingBucketRow[]>([]);
  const [heatmapData, setHeatmapData] = useState<HeatmapData | null>(null);

  const [byTrafficType, setByTrafficType] = useState<BreakdownRow[]>([]);
  const [byAudienceTemp, setByAudienceTemp] = useState<BreakdownRow[]>([]);
  const [byTrafficSource, setByTrafficSource] = useState<BreakdownRow[]>([]);
  const [byUtmCampaign, setByUtmCampaign] = useState<BreakdownRow[]>([]);
  const [breakdownTab, setBreakdownTab] = useState<"trafficType" | "audienceTemp" | "source" | "utm">("trafficType");

  const [appTimingRows, setAppTimingRows] = useState<AppTimingRow[]>([]);
  const [appCompareRows, setAppCompareRows] = useState<AppCompareRow[]>([]);

  const [repeatData, setRepeatData] = useState<RepeatData | null>(null);
  const [repeatMetric, setRepeatMetric] = useState<"avgDaysToApp" | "avgDaysToClose" | null>(null);
  const [healthChecks, setHealthChecks] = useState<HealthCheck[]>([]);
  const [healthOpen, setHealthOpen] = useState(false);

  const [pipeline, setPipeline] = useState<PipelineData | null>(null);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  const [attendance, setAttendance] = useState<AttendanceData | null>(null);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const fetchAll = useCallback(async (f: ActiveFilters) => {
    setLoading(true);
    setAttendanceLoading(true);
    try {
      const [sumRes, timRes, brkRes, appRes, repRes, hlthRes, attRes] = await Promise.all([
        fetch(buildUrl("/api/webinars/summary", f)),
        fetch(buildUrl("/api/webinars/timing", f)),
        fetch(buildUrl("/api/webinars/breakdown", f)),
        fetch(buildUrl("/api/webinars/applications", f)),
        fetch(buildUrl("/api/webinars/repeat", f)),
        fetch("/api/webinars/health"),
        fetch(buildUrl("/api/webinars/attendance", f)),
      ]);
      const [sum, tim, brk, app, rep, hlth, att] = await Promise.all([
        sumRes.json(), timRes.json(), brkRes.json(),
        appRes.json(), repRes.json(), hlthRes.json(), attRes.json(),
      ]);
      if (sum.success) {
        setKpis(sum.kpis ?? null);
        setCompTable(sum.comparisonTable ?? []);
        if (sum.filterOptions) setFilterOptions(sum.filterOptions);
      }
      if (tim.success) {
        setCohortByDate(tim.cohortByDate ?? []);
        setDaysBeforeBuckets(tim.daysBeforeBuckets ?? []);
        setNoAppCount(tim.noAppCount ?? 0);
        setPitchEngagement(tim.pitchEngagement ?? []);
        setHeatmapData(tim.heatmapData ?? null);
      }
      if (brk.success) {
        setByTrafficType(brk.byTrafficType ?? []);
        setByAudienceTemp(brk.byAudienceTemp ?? []);
        setByTrafficSource(brk.byTrafficSource ?? []);
        setByUtmCampaign(brk.byUtmCampaign ?? []);
      }
      if (app.success) {
        setAppTimingRows(app.appTimingByOptinDate ?? []);
        setAppCompareRows(app.comparisonByTiming ?? []);
      }
      if (rep.success) setRepeatData(rep);
      if (hlth.success) setHealthChecks(hlth.checks ?? []);
      if (att.success) {
        const hasRealData = (att.minuteData ?? []).length > 0;
        setAttendance(hasRealData ? {
          minuteData: att.minuteData ?? [],
          sources: att.sources ?? [],
          kpis: att.kpis ?? { totalAttendees: 0, peakAttendees: 0, peakMinute: 0, at50: 0, pctAt50: 0, biggestDropWindow: "—" },
          insights: att.insights ?? [],
          retentionData: att.retentionData ?? [],
        } : { ...MOCK_ATTENDANCE });
      } else {
        setAttendance({ ...MOCK_ATTENDANCE });
      }
    } finally {
      setLoading(false);
      setAttendanceLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(filters); }, [fetchAll, filters]);

  useEffect(() => {
    setPipelineLoading(true);
    fetch("/api/webinars/pipeline")
      .then(r => r.json())
      .then(d => { if (d.success) setPipeline(d.data); })
      .catch(console.error)
      .finally(() => setPipelineLoading(false));
  }, []);

  function applyFilter(key: keyof ActiveFilters, value: string) {
    setFilters(prev => ({ ...prev, [key]: value }));
  }

  async function handleSync() {
    setSyncing(true); setSyncMsg("");
    try {
      const res = await fetch("/api/webinars/sync", { method: "POST" });
      const data = await res.json();
      setSyncMsg(data.success ? "Sync complete ✓" : `Error: ${data.error}`);
      if (data.success) fetchAll(filters);
    } finally { setSyncing(false); }
  }

  function toggleCompSort(field: string) {
    if (field === compSortField) setCompSortDir(d => d === "asc" ? "desc" : "asc");
    else { setCompSortField(field); setCompSortDir("desc"); }
  }

  const sortedComp = [...compTable].sort((a, b) => {
    const av = a[compSortField as keyof ComparisonRow];
    const bv = b[compSortField as keyof ComparisonRow];
    if (typeof av === "number" && typeof bv === "number")
      return compSortDir === "asc" ? av - bv : bv - av;
    return compSortDir === "asc"
      ? String(av ?? "").localeCompare(String(bv ?? ""))
      : String(bv ?? "").localeCompare(String(av ?? ""));
  });

  const breakdownRows =
    breakdownTab === "trafficType" ? byTrafficType :
    breakdownTab === "audienceTemp" ? byAudienceTemp :
    breakdownTab === "source" ? byTrafficSource :
    byUtmCampaign;

  const breakdownLabel =
    breakdownTab === "trafficType" ? "Traffic Type" :
    breakdownTab === "audienceTemp" ? "Audience Temp" :
    breakdownTab === "source" ? "Traffic Source" : "UTM Campaign";


  if (loading && !kpis) {
    return (
      <div className="p-8 flex items-center justify-center min-h-64">
        <div className="text-muted-foreground animate-pulse text-sm">Loading webinar data…</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">

      {/* ══ 1. HEADER + FILTERS ══════════════════════════════════════════════ */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-foreground">Webinar Performance</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Opt-in → Application → Call → Close — powered by your existing data
            </p>
          </div>
          <div className="flex items-center gap-2">
            {syncMsg && <span className="text-xs text-muted-foreground">{syncMsg}</span>}
            <button
              onClick={handleSync}
              disabled={syncing}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--brand-primary)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {syncing ? "Syncing…" : "Sync Sheets"}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Webinar</label>
            <select value={filters.webinarId} onChange={e => applyFilter("webinarId", e.target.value)}
              className="text-sm bg-card border rounded-lg px-2 py-1.5 text-foreground min-w-[160px]">
              <option value="all">All webinars</option>
              {filterOptions.webinars.map(w => (
                <option key={w.id} value={w.id}>
                  {w.date ? `${w.date.slice(0, 10)} – ` : ""}{w.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">From</label>
            <input type="date" value={filters.startDate} onChange={e => applyFilter("startDate", e.target.value)}
              className="text-sm bg-card border rounded-lg px-2 py-1.5 text-foreground" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">To</label>
            <input type="date" value={filters.endDate} onChange={e => applyFilter("endDate", e.target.value)}
              className="text-sm bg-card border rounded-lg px-2 py-1.5 text-foreground" />
          </div>
          {filterOptions.trafficTypes.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Traffic Type</label>
              <select value={filters.trafficType} onChange={e => applyFilter("trafficType", e.target.value)}
                className="text-sm bg-card border rounded-lg px-2 py-1.5 text-foreground min-w-[120px]">
                <option value="">All</option>
                {filterOptions.trafficTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
          {filterOptions.audienceTemps.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Audience Temp</label>
              <select value={filters.audienceTemp} onChange={e => applyFilter("audienceTemp", e.target.value)}
                className="text-sm bg-card border rounded-lg px-2 py-1.5 text-foreground min-w-[120px]">
                <option value="">All</option>
                {filterOptions.audienceTemps.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
          {filterOptions.offerAngles.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Offer/Angle</label>
              <select value={filters.offerAngle} onChange={e => applyFilter("offerAngle", e.target.value)}
                className="text-sm bg-card border rounded-lg px-2 py-1.5 text-foreground min-w-[120px]">
                <option value="">All</option>
                {filterOptions.offerAngles.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          )}
          {filterOptions.utmCampaigns.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">UTM Campaign</label>
              <select value={filters.utmCampaign} onChange={e => applyFilter("utmCampaign", e.target.value)}
                className="text-sm bg-card border rounded-lg px-2 py-1.5 text-foreground min-w-[140px]">
                <option value="">All</option>
                {filterOptions.utmCampaigns.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          <button
            onClick={() => setFilters(DEFAULT_FILTERS)}
            className="self-end px-3 py-1.5 rounded-lg text-xs font-medium border text-muted-foreground hover:text-foreground hover:border-foreground transition-colors"
          >
            Reset
          </button>
        </div>
      </div>

      {/* ══ INTERNAL TAB NAVIGATION ══════════════════════════════════════════ */}
      <div className="flex gap-1 border-b border-border">
        {(["performance", "comparison"] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setWebinarMainTab(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize transition-colors relative ${
              webinarMainTab === tab
                ? "text-[var(--brand-primary)]"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab === "performance" ? "Performance" : "Comparison"}
            {webinarMainTab === tab && (
              <motion.div
                layoutId="webinar-tab-indicator"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--brand-primary)] rounded-full"
              />
            )}
          </button>
        ))}
      </div>

      {webinarMainTab === "performance" && <>
      {/* ══ 2. EXECUTIVE FUNNEL ══════════════════════════════════════════════ */}
      {kpis && (() => {
        function safeRate(n: number, den: number): string {
          if (!den || den === 0) return "N/A";
          return `${((n / den) * 100).toFixed(1)}%`;
        }

        const funnelStages: Array<{ label: string; value: number | null }> = [
          { label: "Opt-ins",          value: kpis.totalOptins },
          { label: "Attended Webinar", value: kpis.totalAttended },
          { label: "Applications",     value: kpis.totalApplications },
          { label: "Calls Had",        value: kpis.totalCallsHad },
          { label: "Closed Deals",     value: kpis.closedDeals },
        ];

        const conversions: Array<{ label: string; rate: string }> = [
          { label: "Show Rate",   rate: safeRate(kpis.totalAttended,      kpis.totalOptins) },
          { label: "App Rate",    rate: safeRate(kpis.totalApplications,  kpis.totalOptins) },
          { label: "Call Rate",   rate: safeRate(kpis.totalCallsHad,      kpis.totalApplications) },
          { label: "Close Rate",  rate: safeRate(kpis.closedDeals,        kpis.totalCallsHad > 0 ? kpis.totalCallsHad : kpis.totalApplications) },
        ];

        const bottomCards = [
          { label: "Revenue",           value: dollar(kpis.revenue) },
          { label: "Revenue / Opt-in",  value: kpis.totalOptins > 0 ? dollar(kpis.revenue / kpis.totalOptins) : "N/A" },
          { label: "Optin → Close Rate",value: safeRate(kpis.closedDeals, kpis.totalOptins) },
        ];

        return (
          <div className="rounded-2xl border bg-card shadow-sm overflow-hidden">
            {/* Header */}
            <div className="px-6 pt-5 pb-4 border-b border-border/50">
              <h2 className="text-sm font-semibold text-foreground">Executive Summary</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Full funnel from landing page to close</p>
            </div>

            {/* Funnel row */}
            <div className="px-6 py-6 overflow-x-auto">
              <div className="flex items-center gap-0 min-w-max">
                {funnelStages.map((stage, i) => (
                  <div key={stage.label} className="flex items-center gap-0">

                    {/* Stage card */}
                    <div className="flex flex-col items-center w-[140px]">
                      <div className="w-full rounded-xl border bg-background border-border shadow-sm px-4 py-4 text-center">
                        <div className="text-2xl font-bold tabular-nums leading-none text-foreground">
                          {stage.value !== null ? num(stage.value) : "—"}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1.5 leading-tight font-medium">
                          {stage.label}
                        </div>
                      </div>
                    </div>

                    {/* Connector + conversion box */}
                    {i < funnelStages.length - 1 && (
                      <div className="flex flex-col items-center w-[80px] shrink-0">
                        {/* Arrow line */}
                        <div className="flex items-center w-full mb-2">
                          <div className="h-px flex-1 bg-border/70" />
                          <svg width="8" height="10" viewBox="0 0 8 10" className="text-border/70 shrink-0">
                            <path d="M0 0 L8 5 L0 10 Z" fill="currentColor" />
                          </svg>
                        </div>
                        {/* Conversion box */}
                        <div className="rounded-lg px-2 py-1.5 text-center shadow-sm"
                          style={{ background: "var(--foreground)" }}>
                          <div className="text-[11px] font-bold tabular-nums"
                            style={{ color: "var(--background)" }}>
                            {conversions[i].rate}
                          </div>
                        </div>
                        <div className="text-[9px] text-muted-foreground mt-1 text-center leading-tight px-1">
                          {conversions[i].label}
                        </div>
                      </div>
                    )}

                  </div>
                ))}
              </div>
            </div>

            {/* Bottom row — business results */}
            <div className="px-6 pb-5">
              <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border/40">
                {bottomCards.map(c => (
                  <div key={c.label} className="rounded-xl border border-border bg-muted/20 px-4 py-3">
                    <div className="text-[10px] text-muted-foreground font-medium mb-1">{c.label}</div>
                    <div className="text-lg font-bold text-foreground tabular-nums">{c.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══ 2b. TRAFFIC SOURCE FUNNEL ═══════════════════════════════════════ */}
      {kpis && byTrafficSource.length > 0 && (
        <ExecFunnelSection kpis={kpis} sources={byTrafficSource} />
      )}

      {/* ══ 3. WEBINAR COMPARISON TABLE ══════════════════════════════════════ */}
      <Section title="Webinar Comparison">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border">
                {[
                  ["Campaign", "webinarName"],
                  ["Opt-ins", "optins"], ["Apps", "applications"], ["App %", "optinToAppRate"],
                  ["Calls", "callsHad"], ["Call %", "callsHad"],
                  ["Closed", "closedDeals"], ["Close %", "callToCloseRate"], ["Revenue", "revenue"],
                ].map(([label, field]) => (
                  <SortTh key={field} label={label} field={field}
                    sortField={compSortField} sortDir={compSortDir} onSort={toggleCompSort} />
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedComp.map(row => (
                <tr key={row.webinarId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                  <td className="px-3 py-2 font-medium text-foreground max-w-[200px] truncate" title={row.webinarName}>{row.webinarName}</td>
                  <td className="px-3 py-2 text-right">{num(row.optins)}</td>
                  <td className="px-3 py-2 text-right">{num(row.applications)}</td>
                  <td className="px-3 py-2 text-right text-[var(--brand-primary)] font-medium">{pct(row.optinToAppRate)}</td>
                  <td className="px-3 py-2 text-right">{num(row.callsHad)}</td>
                  <td className="px-3 py-2 text-right">{row.applications > 0 ? pct((row.callsHad / row.applications) * 100) : "—"}</td>
                  <td className="px-3 py-2 text-right">{num(row.closedDeals)}</td>
                  <td className="px-3 py-2 text-right text-[var(--brand-primary)] font-medium">{pct(row.callToCloseRate)}</td>
                  <td className="px-3 py-2 text-right font-medium">{dollar(row.revenue)}</td>
                </tr>
              ))}
              {sortedComp.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground text-sm">No data — sync your sheets or check your filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {/* ══ NEW A. WEBINAR CARDS COMPARISON ═════════════════════════════════ */}
      <Section title="Webinar Cards">
        {/* Sort + Compare controls */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-xs text-muted-foreground">Sort:</span>
          {([
            { key: "date",     label: "Newest" },
            { key: "showRate", label: "Show Rate" },
            { key: "appRate",  label: "App Rate" },
            { key: "revenue",  label: "Revenue" },
            { key: "optins",   label: "Opt-ins" },
          ] as const).map(s => (
            <button key={s.key} onClick={() => setCardSort(s.key)}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${cardSort === s.key ? "bg-[var(--brand-primary)] text-white" : "border text-muted-foreground hover:text-foreground"}`}>
              {s.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            {selectedCards.size >= 2 && (
              <button onClick={() => setCompareMode(m => !m)}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${compareMode ? "bg-[var(--brand-primary)] text-white" : "border border-[var(--brand-primary)] text-[var(--brand-primary)]"}`}>
                {compareMode ? "Exit Compare" : `Compare ${selectedCards.size} Selected`}
              </button>
            )}
            {selectedCards.size > 0 && (
              <button onClick={() => setSelectedCards(new Set())} className="px-2 py-1 rounded text-xs border text-muted-foreground hover:text-foreground">
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Compare side-by-side view */}
        {compareMode && selectedCards.size >= 2 && (() => {
          const selected = compTable.filter(r => selectedCards.has(r.webinarId));
          const fields: Array<{ label: string; fn: (r: ComparisonRow) => string; highlight?: boolean }> = [
            { label: "Date",          fn: r => r.webinarDate ?? "—" },
            { label: "Audience",      fn: r => r.audience || "—" },
            { label: "Offer/Angle",   fn: r => r.offerAngle || "—" },
            { label: "Opt-ins",       fn: r => num(r.optins) },
            { label: "Attended",      fn: r => num(r.attended) },
            { label: "Show Rate",     fn: r => pct(r.showRate),            highlight: true },
            { label: "Avg Watch",     fn: r => mins(r.avgDuration) },
            { label: "Pitch %",       fn: r => pct(r.reachedPitchRate) },
            { label: "End Pitch %",   fn: r => pct(r.reachedEndOfPitchRate) },
            { label: "Stayed 5m %",   fn: r => pct(r.stayed5Rate) },
            { label: "Applications",  fn: r => num(r.applications) },
            { label: "Optin→App %",   fn: r => pct(r.optinToAppRate),      highlight: true },
            { label: "Calls Had",     fn: r => num(r.callsHad) },
            { label: "Call Rate",     fn: r => r.applications > 0 ? pct((r.callsHad / r.applications) * 100) : "—" },
            { label: "Closed Deals",  fn: r => num(r.closedDeals) },
            { label: "Close Rate",    fn: r => pct(r.callToCloseRate),      highlight: true },
            { label: "Revenue",       fn: r => dollar(r.revenue),           highlight: true },
          ];
          return (
            <div className="mb-6 overflow-x-auto rounded-lg border border-[var(--brand-primary)]/30 bg-muted/20 p-4">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Side-by-Side Comparison</h3>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Metric</th>
                    {selected.map(r => (
                      <th key={r.webinarId} className="px-3 py-2 text-right text-xs font-semibold text-foreground max-w-[140px] truncate">{r.webinarName}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fields.map(f => (
                    <tr key={f.label} className="border-b border-border/40 hover:bg-muted/20">
                      <td className="px-3 py-1.5 text-xs text-muted-foreground">{f.label}</td>
                      {selected.map(r => (
                        <td key={r.webinarId} className={`px-3 py-1.5 text-right text-xs font-medium ${f.highlight ? "text-[var(--brand-primary)]" : "text-foreground"}`}>
                          {f.fn(r)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* Cards grid */}
        {(() => {
          const maxShowRate   = Math.max(0, ...compTable.map(r => r.showRate));
          const maxAppRate    = Math.max(0, ...compTable.map(r => r.optinToAppRate));
          const maxRevenue    = Math.max(0, ...compTable.map(r => r.revenue));
          const maxPitchRate  = Math.max(0, ...compTable.map(r => r.reachedPitchRate));

          const sorted = [...compTable].sort((a, b) => {
            switch (cardSort) {
              case "showRate": return b.showRate - a.showRate;
              case "appRate":  return b.optinToAppRate - a.optinToAppRate;
              case "revenue":  return b.revenue - a.revenue;
              case "optins":   return b.optins - a.optins;
              default: return (b.webinarDate ?? "").localeCompare(a.webinarDate ?? "");
            }
          });

          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sorted.map(row => {
                const isSelected = selectedCards.has(row.webinarId);
                const badges: string[] = [];
                if (row.showRate    >= maxShowRate  && maxShowRate  > 0) badges.push("Best Show Rate");
                if (row.optinToAppRate >= maxAppRate  && maxAppRate  > 0) badges.push("Best App Rate");
                if (row.revenue     >= maxRevenue   && maxRevenue   > 0) badges.push("Highest Revenue");
                if (row.reachedPitchRate >= maxPitchRate && maxPitchRate > 0) badges.push("Top Pitch Engagement");

                return (
                  <motion.div
                    key={row.webinarId}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => {
                      setSelectedCards(prev => {
                        const next = new Set(prev);
                        if (next.has(row.webinarId)) next.delete(row.webinarId);
                        else if (next.size < 4) next.add(row.webinarId);
                        return next;
                      });
                    }}
                    className={`rounded-xl border bg-card p-4 cursor-pointer transition-all ${isSelected ? "border-[var(--brand-primary)] shadow-md" : "hover:border-border"}`}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-foreground truncate">{row.webinarName}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{row.webinarDate ?? "No date"}</div>
                        {row.audience && <div className="text-xs text-muted-foreground">{row.audience}</div>}
                        {row.offerAngle && <div className="text-xs text-muted-foreground italic">{row.offerAngle}</div>}
                      </div>
                      {isSelected && (
                        <div className="w-5 h-5 rounded-full bg-[var(--brand-primary)] flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                    </div>

                    {/* Badges */}
                    {badges.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {badges.map(b => (
                          <span key={b} className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white">
                            {b}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* V-Shaped Funnel */}
                    {(() => {
                      const sp = (a: number, b: number) => b > 0 ? (a / b) * 100 : 0;
                      const stages: Array<{
                        label: string; count: number;
                        rate: number | null; rateName: string | null;
                      }> = [
                        { label: "Opt-ins",      count: row.optins,       rate: null,                               rateName: null },
                        { label: "Applications", count: row.applications, rate: row.optinToAppRate,                 rateName: "App Rate" },
                        { label: "Calls Had",    count: row.callsHad,     rate: sp(row.callsHad, row.applications),  rateName: "Call Rate" },
                        { label: "Closed",       count: row.closedDeals,  rate: row.callToCloseRate,                rateName: "Close Rate" },
                      ];

                      // Diagnostics
                      const convs = stages.slice(1).map((s, i) => ({
                        name: `${stages[i].label} → ${s.label}`,
                        rate: s.rate ?? 0,
                        lost: 100 - (s.rate ?? 0),
                      }));
                      const biggestDrop = convs.length > 0 ? convs.reduce((a, b) => b.lost > a.lost ? b : a) : null;
                      const strongestStep = convs.length > 0 ? convs.reduce((a, b) => b.rate > a.rate ? b : a) : null;

                      return (
                        <div>
                          {stages.map((stage, i) => {
                            const widthPct = row.optins > 0
                              ? Math.max(12, Math.min(100, (stage.count / row.optins) * 100))
                              : 100;
                            const lostPct = stage.rate !== null ? (100 - stage.rate) : null;
                            return (
                              <div key={stage.label}>
                                {/* Connector: conversion + drop-off between stages */}
                                {i > 0 && (
                                  <div className="flex flex-col items-center py-1 gap-0.5">
                                    <div className="text-[10px] font-semibold text-[var(--brand-primary)]">
                                      ↓ {stage.rate !== null ? pct(stage.rate) : "—"} {stage.rateName}
                                    </div>
                                    {lostPct !== null && lostPct > 1 && (
                                      <div className="text-[9px] text-red-400/70">
                                        {pct(lostPct)} lost
                                      </div>
                                    )}
                                  </div>
                                )}
                                {/* Funnel bar — narrows downward */}
                                <div className="flex justify-center">
                                  <div
                                    className="flex items-center justify-between px-2.5 py-1.5 rounded transition-all"
                                    style={{
                                      width: `${widthPct}%`,
                                      background: `linear-gradient(135deg, ${branding.colors.gradient.start}${i === 0 ? "cc" : i === 1 ? "aa" : i === 2 ? "88" : "66"}, ${branding.colors.gradient.end}${i === 0 ? "cc" : i === 1 ? "aa" : i === 2 ? "88" : "66"})`,
                                    }}
                                  >
                                    <span className="text-[10px] text-white font-medium truncate">{stage.label}</span>
                                    <span className="text-[10px] text-white font-bold ml-1 tabular-nums">{num(stage.count)}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {/* Diagnostics */}
                          <div className="mt-3 pt-3 border-t border-border/40 space-y-1">
                            {biggestDrop && (
                              <div className="text-[10px] text-muted-foreground flex items-start gap-1">
                                <span className="text-red-400 flex-shrink-0">●</span>
                                <span>Biggest drop: <strong className="text-foreground">{biggestDrop.name}</strong> ({pct(biggestDrop.lost)} lost)</span>
                              </div>
                            )}
                            {strongestStep && (
                              <div className="text-[10px] text-muted-foreground flex items-start gap-1">
                                <span className="text-green-400 flex-shrink-0">●</span>
                                <span>Strongest: <strong className="text-foreground">{strongestStep.name}</strong> ({pct(strongestStep.rate)})</span>
                              </div>
                            )}
                            {row.revenue > 0 && (
                              <div className="text-[10px] text-muted-foreground flex items-center justify-between pt-1">
                                <span>Revenue</span>
                                <span className="font-bold text-[var(--brand-primary)]">{dollar(row.revenue)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </motion.div>
                );
              })}
              {sorted.length === 0 && (
                <div className="col-span-3 py-8 text-center text-muted-foreground text-sm">No webinar data</div>
              )}
            </div>
          );
        })()}
        {compTable.length > 0 && !compareMode && (
          <p className="text-xs text-muted-foreground mt-3">Click cards to select (up to 4) for side-by-side comparison</p>
        )}
      </Section>

      {/* ══ NEW C. ATTENDANCE BY MINUTE × SOURCE ════════════════════════════ */}
      <Section title="Attendance by Minute by Opt-in Source">
        <div className="flex items-center gap-3 mb-4">
          <p className="text-xs text-muted-foreground flex-1">
            Active viewers over the course of the webinar, broken down by opt-in source.
            Each area shows how many attendees from that source were still watching at each minute.
          </p>
          {attendance?.isMock && (
            <span className="shrink-0 text-[10px] font-semibold px-2 py-1 rounded-full bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border border-yellow-500/30">
              ⚠ Mock Data
            </span>
          )}
        </div>

        {attendanceLoading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="text-sm text-muted-foreground animate-pulse">Loading attendance data…</div>
          </div>
        ) : !attendance || attendance.minuteData.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border">
            <div className="text-sm font-medium text-muted-foreground">No Zoom attendance data found</div>
            <div className="text-xs text-muted-foreground text-center max-w-sm">
              Sync your Zoom_Attendance sheet to see minute-by-minute viewer retention and source breakdown.
              Currently showing {attendance?.kpis.totalAttendees ?? 0} attended records.
            </div>
          </div>
        ) : (
          <>
            {/* KPI chips */}
            <div className="flex flex-wrap gap-3 mb-5">
              {[
                { label: "Total Attendees",      value: num(attendance.kpis.totalAttendees) },
                { label: "Peak Viewers",          value: num(attendance.kpis.peakAttendees), highlight: true },
                { label: "Minute of Peak",        value: `min ${attendance.kpis.peakMinute}` },
                { label: "Viewers at Min 50",     value: num(attendance.kpis.at50) },
                { label: "% of Peak at Min 50",   value: `${attendance.kpis.pctAt50}%`, highlight: true },
                { label: "Biggest Drop Window",   value: attendance.kpis.biggestDropWindow },
              ].map(chip => (
                <div key={chip.label}
                  className={`rounded-lg border px-3 py-2 flex flex-col gap-0.5 ${chip.highlight ? "border-[var(--brand-primary)]/40" : "border-border"}`}>
                  <span className="text-[10px] text-muted-foreground">{chip.label}</span>
                  <span className={`text-sm font-bold ${chip.highlight ? "text-[var(--brand-primary)]" : "text-foreground"}`}>{chip.value}</span>
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 mb-3">
              {attendance.sources.map((src, i) => (
                <div key={src} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: getSourceColor(src, i) }} />
                  {src}
                </div>
              ))}
            </div>

            {/* Stacked area chart */}
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={attendance.minuteData} margin={{ top: 4, right: 24, left: 0, bottom: 0 }}>
                <defs>
                  {attendance.sources.map((src, i) => (
                    <linearGradient key={src} id={`att-grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={getSourceColor(src, i)} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={getSourceColor(src, i)} stopOpacity={0.2} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="minute"
                  tick={{ fontSize: 11 }}
                  label={{ value: "Minute", position: "insideBottom", offset: -2, fontSize: 10 }}
                />
                <YAxis tick={{ fontSize: 11 }} label={{ value: "Active Viewers", angle: -90, position: "insideLeft", fontSize: 10 }} />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const total = payload.reduce((s, p) => s + (Number(p.value) || 0), 0);
                    return (
                      <div className="rounded-lg border bg-card shadow-lg p-3 text-xs min-w-[160px]">
                        <div className="font-semibold text-foreground mb-2">Minute {label}</div>
                        <div className="font-medium text-muted-foreground mb-1.5">Total: {total} viewers</div>
                        {payload.map(p => (
                          <div key={p.dataKey} className="flex items-center justify-between gap-3 py-0.5">
                            <div className="flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.fill ?? p.color }} />
                              <span className="text-muted-foreground">{p.dataKey}</span>
                            </div>
                            <div className="font-medium tabular-nums">
                              {p.value}
                              {total > 0 && <span className="text-muted-foreground ml-1">({Math.round((Number(p.value) / total) * 100)}%)</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  }}
                />
                <ReferenceLine
                  x={50}
                  stroke="var(--brand-primary)"
                  strokeDasharray="5 3"
                  strokeWidth={1.5}
                  label={{ value: "Pitch", position: "top", fontSize: 10, fill: "var(--brand-primary)" }}
                />
                {attendance.sources.map((src, i) => (
                  <Area
                    key={src}
                    type="monotone"
                    dataKey={src}
                    stackId="1"
                    stroke={getSourceColor(src, i)}
                    fill={`url(#att-grad-${i})`}
                    strokeWidth={1.5}
                    isAnimationActive={false}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>

            {/* Auto insights */}
            {attendance.insights.length > 0 && (
              <div className="mt-4 rounded-lg border border-[var(--brand-primary)]/20 bg-[var(--brand-primary)]/5 p-4">
                <div className="text-xs font-semibold text-[var(--brand-primary)] mb-2 uppercase tracking-wide">Insights</div>
                <ul className="space-y-1">
                  {attendance.insights.map((ins, i) => (
                    <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                      <span className="text-[var(--brand-primary)] flex-shrink-0 mt-0.5">›</span>
                      {ins}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Retention chart */}
            <div className="mt-6">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Attendance Retention by Source
              </h3>
              <p className="text-xs text-muted-foreground mb-3">
                Each line shows what % of that source's starting audience is still watching at each minute.
                Answers: which source retained viewers best?
              </p>
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={attendance.retentionData} margin={{ top: 4, right: 24, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="minute" tick={{ fontSize: 11 }} label={{ value: "Minute", position: "insideBottom", offset: -2, fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} domain={[0, 100]}
                    label={{ value: "% Retained", angle: -90, position: "insideLeft", fontSize: 10 }} />
                  <Tooltip
                    formatter={(value: number, name: string) => [`${value}%`, name]}
                    labelFormatter={label => `Minute ${label}`}
                  />
                  <Legend />
                  <ReferenceLine
                    x={50}
                    stroke="var(--brand-primary)"
                    strokeDasharray="5 3"
                    strokeWidth={1.5}
                    label={{ value: "Pitch", position: "top", fontSize: 10, fill: "var(--brand-primary)" }}
                  />
                  {attendance.sources.map((src, i) => (
                    <Line
                      key={src}
                      type="monotone"
                      dataKey={src}
                      stroke={getSourceColor(src, i)}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </Section>

      {/* ══ 5. OPT-IN TIMING IMPACT CHART ═══════════════════════════════════ */}
      {(() => {
        const safePctFE = (a: number, b: number) => b > 0 ? +((a / b) * 100).toFixed(1) : null;

        const bestShowRate  = daysBeforeBuckets.length > 0
          ? daysBeforeBuckets.reduce((a, b) => (b.showRate > a.showRate ? b : a))
          : null;
        const bestAppRate   = daysBeforeBuckets.length > 0
          ? daysBeforeBuckets.reduce((a, b) => (b.optinToAppRate > a.optinToAppRate ? b : a))
          : null;
        const bestCloseRate = daysBeforeBuckets.length > 0
          ? daysBeforeBuckets.reduce((a, b) => {
              const ra = safePctFE(a.closedDeals, a.optins) ?? 0;
              const rb = safePctFE(b.closedDeals, b.optins) ?? 0;
              return rb > ra ? b : a;
            })
          : null;

        const chartData = daysBeforeBuckets.map(r => ({
          ...r,
          closeRate: safePctFE(r.closedDeals, r.optins) ?? 0,
        }));

        return (
          <Section title="Opt-in Timing Impact on Webinar Conversion">
            <p className="text-xs text-muted-foreground mb-5">
              Shows how many days before applying people opted in, and how timing affects attendance, applications, and closed deals.
            </p>

            {/* Insight cards */}
            {daysBeforeBuckets.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                {[
                  {
                    label: "Best Show Rate Timing",
                    row: bestShowRate,
                    value: bestShowRate ? `${bestShowRate.showRate.toFixed(1)}%` : "N/A",
                    sub: bestShowRate ? `show rate from ${num(bestShowRate.optins)} opt-ins` : "",
                    color: CHART[1],
                  },
                  {
                    label: "Best Application Rate Timing",
                    row: bestAppRate,
                    value: bestAppRate ? `${bestAppRate.optinToAppRate.toFixed(1)}%` : "N/A",
                    sub: bestAppRate ? `app rate from ${num(bestAppRate.optins)} opt-ins` : "",
                    color: CHART[2],
                  },
                  {
                    label: "Best Close Rate Timing",
                    row: bestCloseRate,
                    value: bestCloseRate ? `${(safePctFE(bestCloseRate.closedDeals, bestCloseRate.optins) ?? 0).toFixed(1)}%` : "N/A",
                    sub: bestCloseRate ? `close rate from ${num(bestCloseRate.optins)} opt-ins` : "",
                    color: CHART[0],
                  },
                ].map(card => (
                  <div key={card.label}
                    className="rounded-xl border bg-muted/20 p-4"
                    style={{ borderColor: `${card.color}40` }}
                  >
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70 mb-1">{card.label}</div>
                    <div className="text-base font-black tabular-nums" style={{ color: card.color }}>
                      {card.value}
                    </div>
                    {card.row && (
                      <div className="text-xs font-semibold text-foreground mt-0.5">{card.row.bucket} before</div>
                    )}
                    {card.sub && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">{card.sub}</div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* No-app summary card */}
            {noAppCount > 0 && (
              <div className="mb-5 flex items-center gap-3 rounded-lg border border-border/50 bg-muted/20 px-4 py-2.5">
                <span className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{num(noAppCount)}</span> opt-ins had no application — excluded from the timing chart below.
                </span>
              </div>
            )}

            {daysBeforeBuckets.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={chartData} margin={{ top: 8, right: 32, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.5} />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" orientation="left" tick={{ fontSize: 11 }}
                      label={{ value: "Opt-ins", angle: -90, position: "insideLeft", fontSize: 10, offset: 10 }} />
                    <YAxis yAxisId="right" orientation="right"
                      tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }}
                      domain={[0, "auto"]} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const r = daysBeforeBuckets.find(x => x.bucket === label);
                        if (!r) return null;
                        const closeRateVal = r.optins > 0 ? ((r.closedDeals / r.optins) * 100).toFixed(1) : "N/A";
                        const revPerOptin  = r.optins > 0 && r.revenue > 0 ? dollar(r.revenue / r.optins) : "N/A";
                        return (
                          <div className="rounded-xl border border-border/50 bg-card/98 backdrop-blur-sm p-3 shadow-2xl text-xs" style={{ minWidth: 200 }}>
                            <div className="font-bold text-sm text-foreground mb-2">{label}</div>
                            <div className="space-y-1">
                              {[
                                ["Opt-ins",          num(r.optins)],
                                ["Attended",         num(r.attended)],
                                ["Show Rate",        r.optins > 0 ? `${r.showRate.toFixed(1)}%` : "N/A"],
                                ["Applications",     num(r.applications)],
                                ["Application Rate", r.optins > 0 ? `${r.optinToAppRate.toFixed(1)}%` : "N/A"],
                                ["Closed",           num(r.closedDeals)],
                                ["Close Rate",       closeRateVal + (closeRateVal !== "N/A" ? "%" : "")],
                                ["Revenue",          r.revenue > 0 ? dollar(r.revenue) : "N/A"],
                                ["Rev / Opt-in",     revPerOptin],
                              ].map(([k, v]) => (
                                <div key={k} className="flex justify-between gap-4">
                                  <span className="text-muted-foreground">{k}</span>
                                  <span className="font-semibold text-foreground tabular-nums">{v}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="left" dataKey="optins" name="Opt-ins" fill={CHART[0]} opacity={0.75} radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="showRate"      name="Show Rate %"        stroke={CHART[1]} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    <Line yAxisId="right" type="monotone" dataKey="optinToAppRate" name="Application Rate %" stroke={CHART[2]} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    <Line yAxisId="right" type="monotone" dataKey="closeRate"     name="Close Rate %"       stroke={CHART[3]} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} strokeDasharray="5 3" />
                  </ComposedChart>
                </ResponsiveContainer>

                {/* Table */}
                <div className="mt-6 overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-border">
                        {[
                          ["Timing",           true],
                          ["Opt-ins",          false],
                          ["Attended",         false],
                          ["Show Rate",        false],
                          ["Applications",     false],
                          ["App Rate",         false],
                          ["Closed",           false],
                          ["Close Rate",       false],
                          ["Revenue",          false],
                          ["Rev / Opt-in",     false],
                        ].map(([h, left]) => (
                          <th key={h as string}
                            className={`px-3 py-2 text-xs font-semibold text-muted-foreground ${left ? "text-left" : "text-right"}`}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {daysBeforeBuckets.map(row => {
                        const closeRateDisp = row.optins > 0
                          ? `${((row.closedDeals / row.optins) * 100).toFixed(1)}%`
                          : "N/A";
                        const revPerOptin = row.optins > 0 && row.revenue > 0
                          ? dollar(row.revenue / row.optins)
                          : "N/A";
                        return (
                          <tr key={row.bucket} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                            <td className="px-3 py-2 font-semibold text-foreground">{row.bucket}</td>
                            <td className="px-3 py-2 text-right">{num(row.optins)}</td>
                            <td className="px-3 py-2 text-right">{num(row.attended)}</td>
                            <td className="px-3 py-2 text-right text-[var(--brand-primary)] font-medium">
                              {row.optins > 0 ? `${row.showRate.toFixed(1)}%` : "N/A"}
                            </td>
                            <td className="px-3 py-2 text-right">{num(row.applications)}</td>
                            <td className="px-3 py-2 text-right text-[var(--brand-primary)] font-medium">
                              {row.optins > 0 ? `${row.optinToAppRate.toFixed(1)}%` : "N/A"}
                            </td>
                            <td className="px-3 py-2 text-right">{num(row.closedDeals)}</td>
                            <td className="px-3 py-2 text-right font-medium">{closeRateDisp}</td>
                            <td className="px-3 py-2 text-right">{row.revenue > 0 ? dollar(row.revenue) : "—"}</td>
                            <td className="px-3 py-2 text-right">{revPerOptin}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="text-center text-muted-foreground py-8 text-sm">No timing data available</p>
            )}
          </Section>
        );
      })()}

      {/* ══ 6. WEBINAR TIMING HEATMAP ════════════════════════════════════════ */}
      {(() => {
        // Collect all webinar names for tooltips
        const webinarNameMap = new Map(heatmapData?.webinars.map(w => [w.id, w.name]) ?? []);

        return (
          <Section title="Webinar Timing Heatmap">
            <p className="text-xs text-muted-foreground mb-5">
              Compare how opt-in timing affects show rate across webinars.
            </p>

            {heatmapData && heatmapData.webinars.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="text-xs border-collapse" style={{ width: "100%", borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-[11px] font-bold text-muted-foreground whitespace-nowrap sticky left-0 bg-card z-10"
                        style={{ minWidth: 180 }}>
                        Campaign
                      </th>
                      {heatmapData.buckets.map(b => (
                        <th key={b}
                          className="px-3 py-3 text-center text-[11px] font-bold text-muted-foreground whitespace-nowrap border-b border-border/40"
                          style={{ minWidth: 96 }}>
                          {b}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapData.webinars.map((w, wIdx) => (
                      <tr key={w.id}
                        className={wIdx % 2 === 0 ? "bg-muted/10" : ""}
                      >
                        {/* Campaign name cell */}
                        <td
                          className="px-4 py-3 font-semibold text-foreground whitespace-nowrap sticky left-0 z-10 border-r border-border/30"
                          style={{
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            background: wIdx % 2 === 0 ? "var(--muted)" : "var(--card)",
                          }}
                          title={w.name}
                        >
                          {w.name}
                        </td>

                        {/* Data cells */}
                        {heatmapData.buckets.map(bucket => {
                          const cell = heatmapData.cells[w.id]?.[bucket];
                          const optins      = cell?.optins      ?? 0;
                          const attended    = cell?.attended    ?? 0;
                          const applications = cell?.applications ?? 0;
                          const closedDeals  = cell?.closedDeals  ?? 0;
                          const revenue      = cell?.revenue      ?? 0;
                          const showRate     = cell?.showRate     ?? 0;
                          const appRate      = cell?.optinToAppRate ?? 0;
                          const closeRate    = optins > 0 ? (closedDeals / optins) * 100 : 0;
                          const revPerOptin  = optins > 0 && revenue > 0 ? revenue / optins : 0;

                          // Soft brand-primary gradient: low alpha at low show rate, higher at high
                          const bgAlpha = optins > 0 ? Math.min(0.06 + (showRate / 100) * 0.44, 0.50) : 0;

                          const tooltipLines: Array<[string, string]> = [
                            ["Opt-ins",          num(optins)],
                            ["Attended",         num(attended)],
                            ["Show Rate",        optins > 0 ? `${showRate.toFixed(1)}%` : "N/A"],
                            ["Applications",     num(applications)],
                            ["Application Rate", optins > 0 ? `${appRate.toFixed(1)}%` : "N/A"],
                            ["Closed",           num(closedDeals)],
                            ["Close Rate",       optins > 0 ? `${closeRate.toFixed(1)}%` : "N/A"],
                            ["Revenue",          revenue > 0 ? dollar(revenue) : "N/A"],
                            ["Rev / Opt-in",     revPerOptin > 0 ? dollar(revPerOptin) : "N/A"],
                          ];

                          return (
                            <td
                              key={bucket}
                              className="px-3 py-3 text-center align-middle border-b border-border/20 group relative transition-all duration-100"
                              style={{
                                backgroundColor: optins > 0
                                  ? `color-mix(in srgb, var(--brand-primary) ${Math.round(bgAlpha * 100)}%, transparent)`
                                  : "transparent",
                              }}
                            >
                              {optins > 0 ? (
                                <>
                                  <div className="font-bold text-[12px] text-foreground leading-tight">
                                    {showRate.toFixed(1)}%
                                  </div>
                                  <div className="text-[10px] text-muted-foreground/60 mt-0.5 leading-none">
                                    n={optins.toLocaleString()}
                                  </div>

                                  {/* Hover tooltip */}
                                  <div className="absolute z-50 hidden group-hover:flex flex-col
                                    bottom-full left-1/2 -translate-x-1/2 mb-2 pointer-events-none"
                                    style={{ width: 220 }}>
                                    <div className="rounded-xl border border-border/50 bg-card shadow-2xl p-3 text-left">
                                      <div className="font-bold text-[12px] text-foreground mb-0.5 leading-tight truncate">
                                        {webinarNameMap.get(w.id) ?? w.name}
                                      </div>
                                      <div className="text-[10px] text-muted-foreground mb-2">{bucket}</div>
                                      <div className="space-y-1">
                                        {tooltipLines.map(([k, v]) => (
                                          <div key={k} className="flex justify-between gap-3 text-[11px]">
                                            <span className="text-muted-foreground">{k}</span>
                                            <span className="font-semibold text-foreground tabular-nums">{v}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    {/* Arrow */}
                                    <div className="self-center w-2.5 h-2.5 rotate-45 border-r border-b border-border/50 bg-card -mt-[6px]" />
                                  </div>
                                </>
                              ) : (
                                <span className="text-muted-foreground/20 text-[11px]">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8 text-sm">No heatmap data available</p>
            )}
          </Section>
        );
      })()}

      {/* ══ 7. APPLICATION RATE HEATMAP ══════════════════════════════════════ */}
      {(() => {
        const webinarNameMapApp = new Map(heatmapData?.webinars.map(w => [w.id, w.name]) ?? []);
        return (
          <Section title="Application Rate Heatmap">
            <p className="text-xs text-muted-foreground mb-5">
              Compare how opt-in timing affects application rate across webinars.
            </p>
            {heatmapData && heatmapData.webinars.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="text-xs border-collapse" style={{ width: "100%", borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-[11px] font-bold text-muted-foreground whitespace-nowrap sticky left-0 bg-card z-10"
                        style={{ minWidth: 180 }}>
                        Campaign
                      </th>
                      {heatmapData.buckets.map(b => (
                        <th key={b}
                          className="px-3 py-3 text-center text-[11px] font-bold text-muted-foreground whitespace-nowrap border-b border-border/40"
                          style={{ minWidth: 96 }}>
                          {b}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapData.webinars.map((w, wIdx) => (
                      <tr key={w.id} className={wIdx % 2 === 0 ? "bg-muted/10" : ""}>
                        <td
                          className="px-4 py-3 font-semibold text-foreground whitespace-nowrap sticky left-0 z-10 border-r border-border/30"
                          style={{
                            maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis",
                            background: wIdx % 2 === 0 ? "var(--muted)" : "var(--card)",
                          }}
                          title={w.name}
                        >
                          {w.name}
                        </td>
                        {heatmapData.buckets.map(bucket => {
                          const cell = heatmapData.cells[w.id]?.[bucket];
                          const optins       = cell?.optins       ?? 0;
                          const attended     = cell?.attended     ?? 0;
                          const applications = cell?.applications ?? 0;
                          const closedDeals  = cell?.closedDeals  ?? 0;
                          const revenue      = cell?.revenue      ?? 0;
                          const showRate     = cell?.showRate     ?? 0;
                          const appRate      = cell?.optinToAppRate ?? 0;
                          const closeRate    = optins > 0 ? (closedDeals / optins) * 100 : 0;
                          const revPerOptin  = optins > 0 && revenue > 0 ? revenue / optins : 0;

                          const bgAlpha = optins > 0 ? Math.min(0.06 + (appRate / 100) * 0.44, 0.50) : 0;

                          const tooltipLines: Array<[string, string]> = [
                            ["Opt-ins",          num(optins)],
                            ["Attended",         num(attended)],
                            ["Show Rate",        optins > 0 ? `${showRate.toFixed(1)}%` : "N/A"],
                            ["Applications",     num(applications)],
                            ["Application Rate", optins > 0 ? `${appRate.toFixed(1)}%` : "N/A"],
                            ["Closed",           num(closedDeals)],
                            ["Close Rate",       optins > 0 ? `${closeRate.toFixed(1)}%` : "N/A"],
                            ["Revenue",          revenue > 0 ? dollar(revenue) : "N/A"],
                            ["Rev / Opt-in",     revPerOptin > 0 ? dollar(revPerOptin) : "N/A"],
                          ];

                          return (
                            <td
                              key={bucket}
                              className="px-3 py-3 text-center align-middle border-b border-border/20 group relative transition-all duration-100"
                              style={{
                                backgroundColor: optins > 0
                                  ? `color-mix(in srgb, var(--brand-primary) ${Math.round(bgAlpha * 100)}%, transparent)`
                                  : "transparent",
                              }}
                            >
                              {optins > 0 ? (
                                <>
                                  <div className="font-bold text-[12px] text-foreground leading-tight">
                                    {appRate.toFixed(1)}%
                                  </div>
                                  <div className="text-[10px] text-muted-foreground/60 mt-0.5 leading-none">
                                    n={optins.toLocaleString()}
                                  </div>
                                  <div className="absolute z-50 hidden group-hover:flex flex-col
                                    bottom-full left-1/2 -translate-x-1/2 mb-2 pointer-events-none"
                                    style={{ width: 220 }}>
                                    <div className="rounded-xl border border-border/50 bg-card shadow-2xl p-3 text-left">
                                      <div className="font-bold text-[12px] text-foreground mb-0.5 leading-tight truncate">
                                        {webinarNameMapApp.get(w.id) ?? w.name}
                                      </div>
                                      <div className="text-[10px] text-muted-foreground mb-2">{bucket}</div>
                                      <div className="space-y-1">
                                        {tooltipLines.map(([k, v]) => (
                                          <div key={k} className="flex justify-between gap-3 text-[11px]">
                                            <span className="text-muted-foreground">{k}</span>
                                            <span className="font-semibold text-foreground tabular-nums">{v}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    <div className="self-center w-2.5 h-2.5 rotate-45 border-r border-b border-border/50 bg-card -mt-[6px]" />
                                  </div>
                                </>
                              ) : (
                                <span className="text-muted-foreground/20 text-[11px]">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8 text-sm">No heatmap data available</p>
            )}
          </Section>
        );
      })()}

      {/* ══ 8. PITCH ENGAGEMENT BY TIMING ════════════════════════════════════ */}
      <Section title="Pitch Engagement by Opt-in Timing">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border">
                {["Timing", "Attended", "Reached Pitch", "Pitch %", "End Pitch", "End Pitch %", "Stayed 5m", "Stayed 5m %", "Apps", "Att→App %"].map(h => (
                  <th key={h} className={`px-3 py-2 text-xs font-semibold text-muted-foreground ${h === "Timing" ? "text-left" : "text-right"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pitchEngagement.map(row => (
                <tr key={row.bucket} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                  <td className="px-3 py-2 font-medium">{row.bucket}</td>
                  <td className="px-3 py-2 text-right">{num(row.attended)}</td>
                  <td className="px-3 py-2 text-right">{num(row.reachedPitch)}</td>
                  <td className="px-3 py-2 text-right text-[var(--brand-primary)] font-medium">{pct(row.reachedPitchRate)}</td>
                  <td className="px-3 py-2 text-right">{num(row.reachedEndOfPitch)}</td>
                  <td className="px-3 py-2 text-right">{pct(row.reachedEndOfPitchRate)}</td>
                  <td className="px-3 py-2 text-right">{num(row.stayed5)}</td>
                  <td className="px-3 py-2 text-right">{pct(row.stayed5Rate)}</td>
                  <td className="px-3 py-2 text-right">{num(row.applications)}</td>
                  <td className="px-3 py-2 text-right">{pct(row.attendeeToAppRate)}</td>
                </tr>
              ))}
              {pitchEngagement.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground text-sm">No data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {/* ══ 9. TRAFFIC / AUDIENCE BREAKDOWN ══════════════════════════════════ */}
      <Section title="Traffic & Audience Breakdown">
        <div className="flex gap-1 mb-5 border-b border-border/50">
          {([
            { key: "trafficType", label: "Traffic Type" },
            { key: "audienceTemp", label: "Audience Temp" },
            { key: "source", label: "Traffic Source" },
            { key: "utm", label: "UTM Campaign" },
          ] as const).map(tab => (
            <button key={tab.key} onClick={() => setBreakdownTab(tab.key)}
              className={`px-3 py-2 text-xs font-medium transition-colors relative pb-2.5 ${
                breakdownTab === tab.key
                  ? "text-[var(--brand-primary)]"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label}
              {breakdownTab === tab.key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--brand-primary)] rounded-full" />
              )}
            </button>
          ))}
        </div>
        <BreakdownTable rows={breakdownRows} keyLabel={breakdownLabel} />
      </Section>

      {/* ══ 10. REPEAT BEHAVIOR ══════════════════════════════════════════════ */}
      {repeatData && (() => {
        // Dynamic insight line
        const rows = repeatData.byWebinarsAttended;
        const row1 = rows.find(r => r.webinarsAttended === "1 webinar");
        const row2 = rows.find(r => r.webinarsAttended === "2 webinars");
        const insightLine = (() => {
          if (!row1 || !row2 || row1.people === 0 || row2.people === 0) return null;
          if (row2.appRate > row1.appRate * 1.2) {
            return `People who attend 2 webinars are ${((row2.appRate / row1.appRate - 1) * 100).toFixed(0)}% more likely to apply than those who attend only 1.`;
          }
          if (row2.closeRate > row1.closeRate * 1.1) {
            return `Repeat attendees show a meaningfully higher close rate — attending more webinars correlates with stronger buyer intent.`;
          }
          return `Explore how attending more webinars affects application and close rates below.`;
        })();

        const chartData = rows.map(r => ({
          name: r.webinarsAttended,
          avgDaysToApp: parseFloat((r.avgDaysToApp ?? 0).toFixed(1)),
          avgDaysToClose: parseFloat((r.avgDaysToClose ?? 0).toFixed(1)),
        }));

        return (
          <Section title="Repeat Behavior">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="rounded-lg border bg-muted/30 p-4">
                <div className="text-xs text-muted-foreground mb-1">Repeat Attendees (2+)</div>
                <div className="text-2xl font-bold">{num(repeatData.repeatAttendees)}</div>
                <div className="text-xs text-muted-foreground">people attended 2 or more webinars</div>
              </div>
              <div className="rounded-lg border bg-muted/30 p-4">
                <div className="text-xs text-muted-foreground mb-1">Avg Webinars Before Application</div>
                <div className="text-2xl font-bold text-[var(--brand-primary)]">{repeatData.avgWebinarsBeforeApp.toFixed(1)}</div>
                <div className="text-xs text-muted-foreground">avg webinars attended by applicants</div>
              </div>
              <div className="rounded-lg border bg-muted/30 p-4">
                <div className="text-xs text-muted-foreground mb-1">Avg Webinars Before Close</div>
                <div className="text-2xl font-bold text-[var(--brand-primary)]">{repeatData.avgWebinarsBeforeClose.toFixed(1)}</div>
                <div className="text-xs text-muted-foreground">avg webinars attended by closed deals</div>
              </div>
            </div>

            {/* Chart */}
            <div className="mb-6">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Days to Conversion by Webinars Attended</div>
              <div className="flex gap-2 mb-4">
                {([
                  { key: "avgDaysToApp" as const, label: "Avg Days to Application" },
                  { key: "avgDaysToClose" as const, label: "Avg Days to Close" },
                ]).map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setRepeatMetric(prev => prev === opt.key ? null : opt.key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                      repeatMetric === opt.key
                        ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {repeatMetric ? (
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${v.toFixed(0)}d`} />
                    <Tooltip
                      contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }}
                      formatter={(value: number) => [`${value.toFixed(1)} days`, repeatMetric === "avgDaysToApp" ? "Avg Days to Application" : "Avg Days to Close"]}
                    />
                    <Line type="monotone" dataKey={repeatMetric} stroke={branding.colors.chart[0]} strokeWidth={2} dot={{ r: 4, fill: branding.colors.chart[0] }} activeDot={{ r: 5 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[200px] flex items-center justify-center rounded-lg border border-dashed border-border">
                  <p className="text-sm text-muted-foreground">Select a metric above to view the chart</p>
                </div>
              )}
            </div>

            {/* Insight line */}
            {insightLine && (
              <p className="text-sm text-muted-foreground bg-muted/30 rounded-lg px-4 py-3 mb-4 border border-border/50">
                💡 {insightLine}
              </p>
            )}

            {/* Table */}
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-border">
                  {["Webinars Attended", "People", "Application Rate", "Close Rate", "Closed"].map(h => (
                    <th key={h} className={`px-3 py-2 text-xs font-semibold text-muted-foreground ${h === "Webinars Attended" ? "text-left" : "text-right"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.webinarsAttended} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="px-3 py-2 font-medium">{row.webinarsAttended}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(row.people)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{row.people > 0 ? pct(row.appRate) : "N/A"}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{row.applied > 0 ? pct(row.closeRate) : "N/A"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(row.closed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        );
      })()}

      {/* ══ 11. DATA HEALTH (collapsible) ════════════════════════════════════ */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <button
          onClick={() => setHealthOpen(o => !o)}
          className="w-full flex items-center justify-between px-5 py-4 text-sm font-semibold text-foreground hover:bg-muted/20 transition-colors"
        >
          <span>Data Health</span>
          <span className="text-muted-foreground text-xs">{healthOpen ? "▲ collapse" : "▼ expand"}</span>
        </button>
        <AnimatePresence>
          {healthOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-5 pb-5 space-y-2 border-t border-border pt-4">
                {healthChecks.map(check => (
                  <div
                    key={check.label}
                    className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm border ${
                      check.severity === "error"
                        ? "bg-red-500/10 border-red-500/30"
                        : check.severity === "warn"
                        ? "bg-yellow-500/10 border-yellow-500/30"
                        : "bg-muted/30 border-border"
                    }`}
                  >
                    <span className={
                      check.severity === "error" ? "text-red-400" :
                      check.severity === "warn" ? "text-yellow-400" :
                      "text-muted-foreground"
                    }>{check.label}</span>
                    <span className={`font-bold tabular-nums ${
                      check.count > 0 && check.severity === "error" ? "text-red-400" :
                      check.count > 0 && check.severity === "warn" ? "text-yellow-400" :
                      "text-foreground"
                    }`}>{num(check.count)}</span>
                  </div>
                ))}
                {healthChecks.length === 0 && (
                  <p className="text-center text-muted-foreground py-4 text-sm">No health checks available</p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>


      </> /* end webinarMainTab === "performance" */}

      {/* ══ COMPARISON TAB ═══════════════════════════════════════════════════ */}
      {webinarMainTab === "comparison" && (
        <WebinarComparisonTab compTable={compTable} />
      )}

    </div>
  );
}
